import type { Dictionary } from '../types'

const dict: Dictionary = {
  common: {
    save: '儲存',
    saving: '儲存中...',
    saved: '已儲存',
    cancel: '取消',
    delete: '刪除',
    confirm: '確認',
    close: '關閉',
    back: '返回',
    next: '下一步',
    done: '完成',
    retry: '重試',
    add: '新增',
    edit: '編輯',
    update: '更新',
    loading: '載入中...',
    search: '搜尋',
    filter: '篩選',
    sort: '排序',
    all: '全部',
    yes: '是',
    no: '否',
    enable: '啟用',
    disable: '停用',
    enabled: '已啟用',
    disabled: '已停用',
    optional: '選填',
    unlimited: '無限',
    unknown: '未知',
    unknownCustomer: '未知客戶',
    noData: '目前無資料',
    comingSoon: '即將推出',
    export: '匯出',
    signOut: '登出',
    admin: '管理員',
    owner: '店主',
    staff: '職員',
    manager: '經理',
    agent: '客服',
    marketer: '行銷',
    finance: '財務',
    viewer: '檢視者',
    viewAll: '查看全部',
    today: '今日',
    more: '更多',
  },

  status: {
    pending: '待處理',
    confirmed: '已確認',
    paid: '已付款',
    completed: '已完成',
    cancelled: '已取消',
    noShow: '未到',
    inProgress: '處理中',
    resolved: '已解決',
    closed: '已關閉',
    listed: '上架中',
    unlisted: '已下架',
    unpaid: '未付款',
  },

  nav: {
    overview: '總覽',
    conversations: '對話',
    orders: '訂單',
    bookings: '預約',
    bookingsFnb: '訂位',
    customers: '客戶',
    products: '產品',
    productsBooking: '服務',
    productsConsultation: '諮詢項目',
    productsSupport: '支援項目',
    catalog: '目錄',
    inquiries: '查詢',
    followUps: '跟進',
    knowledge: '知識庫',
    loyalty: '會員',
    coupons: '優惠券',
    broadcasts: '群發',
    agent: 'AI 客服',
    sandbox: '測試對話',
    sandboxMode: '測試模式',
    storefront: '網店',
    insights: '分析',
    settings: '設定',
    usage: '使用量',
  },

  switcher: {
    needsAttentionPlural: '{count} 個其他品牌需要處理',
    needsAttentionSingular: '另一個品牌需要處理',
    allClear: '其他品牌已清',
    triggerAriaLabel: '切換品牌',
    listboxAriaLabel: '可切換的品牌',
    loading: '載入品牌中…',
    switchError: '切換品牌失敗，請重試',
    notAccessibleError: '沒有權限存取此品牌',
    networkError: '網絡連線失敗',
    unreadBadgeSingular: '{count} 條未處理',
    unreadBadgePlural: '{count} 條未處理',
  },

  overview: {
    title: '總覽',
    subtitle: '您的 AI 助手全天候為您處理客戶對話',
    subtitleBooking: 'AI 24×7 接客 · 預約 · 跟進',
    subtitleBookingFnb: 'AI 24×7 接客 · 訂位 · 跟進',
    subtitleConsultation: 'AI 24×7 接收查詢 · 分流 · 整理客戶資料',
    subtitleSupport: 'AI 24×7 回答常見問題 · 開工單 · 自助分流',
    conversations: '對話',
    orders: '訂單',
    bookings: '預約',
    bookingsFnb: '訂位',
    inquiriesNew: '新查詢',
    inquiriesPending: '待處理',
    revenue: '收入',
    monthlyUsage: '本月 AI credits',
    percentUsed: '% 已使用',
    activeConversations: '進行中對話：{count} 個',
    viewAllConversations: '查看所有對話 →',
    topCustomers: '頂級客戶',
    viewAll: '查看全部',
    aiValueOrders: 'AI 帶來的訂單',
    aiValueInquiries: 'AI 處理查詢',
    aiValueInquiriesSub: '無需轉人工',
  },

  products: {
    title: '產品',
    addProduct: '新增產品 / 服務',
    searchPlaceholder: '搜尋產品名稱...',
    noProducts: '目前無產品',
    noProductsHint: '請新增產品或服務',
    seedSamples: {
      button: '試試範例產品',
      loading: '載入中…',
      hint: '載入一組小型範例產品，您可以隨時修改或刪除。',
      failed: '載入範例失敗',
    },
    product: '產品',
    service: '服務',
    inquiry: '查詢',
    package: '套票',
    packageTypeOption: '套票（多次預約）',
    packageHint: '顧客預付以兌換 N 次指定服務預約，通常比逐次下單便宜。',
    packageTargetService: '對應服務',
    packageTargetServiceHint: '每次兌換時會針對此服務建立一個預約',
    packageSelectTargetService: '請選擇',
    packageTotalSessions: '總堂數',
    packageTotalSessionsPlaceholder: '例如 6',
    packageValidityDays: '有效期（天）',
    packageValidityDaysPlaceholder: '例如 365（留空 = 永久有效）',
    packageValidityLabel: '有效期',
    packageValidityForever: '永久有效',
    packageValidityDaysFormat: '{days} 天',
    packageSessionsShort: '{count} 堂',
    packageRowSessions: '× {count} 堂',
    basicInfo: '基本資料',
    name: '名稱',
    price: '價格',
    pricePlaceholder: '例：280',
    description: '描述',
    descriptionPlaceholder: '簡單描述您的產品或服務...',
    salesSettings: '銷售設定',
    orderingMode: '下單模式',
    directOrder: '直接下單',
    inquiryOnly: '僅供查詢',
    fulfillment: '取貨方式',
    delivery: '配送',
    pickup: '自取',
    deliveryOrPickup: '配送或自取',
    na: '不適用',
    serviceMode: '服務方式',
    inStore: '到店',
    atCustomer: '到府',
    online: '線上',
    hybrid: '彈性',
    paymentTiming: '付款時機',
    fullUpfront: '全額付款',
    deposit: '訂金',
    onCompletion: '完成後付款',
    requiresBooking: '需要預約',
    requiresBookingYes: '客戶需要選擇日期與時間',
    requiresBookingNo: '客戶可直接下單，無需選擇時間',
    depositPercent: '訂金百分比 (%)',
    depositPercentPlaceholder: '例：30',
    priceRange: '價格範圍',
    minPrice: '最低價',
    maxPrice: '最高價',
    quotationNotes: '報價備註（AI 使用）',
    quotationNotesPlaceholder: '例：價格依面積、樓層、工程複雜度而定',
    priceRangeHint: '設定價格範圍後，AI 會主動告知客戶大約價格。報價備註可協助 AI 收集相關資料。',
    pickupAddress: '自取地址',
    pickupAddressPlaceholder: '例：旺角彌敦道123號',
    pickupNotes: '自取備註',
    pickupNotesPlaceholder: '例：請出示訂單編號',
    serviceAddress: '服務地址',
    serviceAddressPlaceholder: '例：銅鑼灣記利佐治街1號',
    serviceDuration: '服務時長（分鐘）',
    serviceDurationPlaceholder: '例：60',
    concurrent: '同時服務人數',
    concurrentDefault: '預設：1',
    creating: '建立中...',
    confirmAdd: '確認新增',
    draftBanner: '此{type}尚未上架，客戶暫時無法看到。準備就緒後請點擊「已下架」按鈕上架。',
    recentOrders: '近期訂單',
    variants: '款式',
    variantName: '款式名稱',
    variantNamePlaceholder: '例：白色 / M',
    variantPrice: '價格（留空 = 使用產品價格）',
    variantPricePlaceholder: '價格',
    variantStock: '庫存（留空 = 無限）',
    variantStockPlaceholder: '庫存',
    sku: 'SKU',
    skuPlaceholder: '例：WHT-M-001',
    addVariant: '新增款式',
    noVariants: '目前無款式',
    noVariantsHint: '尚未設定款式。新增後客戶下單時需要選擇。',
    adding: '新增中...',
    list: '上架',
    unlist: '下架',
    typeLabel: '類型',
    priceHKD: '價格 (HKD)',
    priceInvalid: '價格必須為 0 或以上。',
    minutes: '分鐘',
    createdAt: '建立於',
    totalSales: '總銷量',
    totalRevenue: '總收入',
    orderCount: '訂單數',
    noOrderHistory: '目前無訂單記錄',
    listNow: '立即上架',
    followsProduct: '跟產品',
    stock: '庫存',
    notFound: '找不到產品',
    serviceDurationLabel: '服務時長',
    concurrentLabel: '同時服務人數',
    images: '產品照片',
    uploadImages: '上傳照片',
    noImages: '暫無產品照片',
    noImagesHint: '上傳產品照片，AI 推薦產品時會自動傳送給客人。',
    setPrimary: '設為封面',
    primary: '封面',
    uploading: '上傳中…',
    dragOrClick: '拖放或點擊上傳',
    imageFormats: '支援 JPG、PNG、WebP',
    resourceRequirements: {
      label: '所需資源',
      hint: '設定後，系統會在預約時自動分配空閒資源，資源不足的時段會自動隱藏。',
      itemLabel: '{quantity}× {label}',
      emptyHint: '未設定需求 — 預約不會自動預留房間或設備。',
      noCategories: '請先建立至少一個資源類別，才能新增需求。',
      manageCategories: '管理類別',
      addRequirement: '新增需求',
      modeCategory: '依類別',
      modeSpecific: '指定資源',
      modeHintCategory: '類別中任何一個空閒資源皆可。',
      modeHintSpecific: '指定具體哪幾台／哪幾間可用（例如「A 機或 B 機」）。',
      selectCategory: '選擇類別',
      selectResources: '選擇資源',
      quantity: '數量',
      specificSummary: '指定 {count} 個資源',
      save: '儲存',
      cancel: '取消',
      errAdd: '加入失敗',
      errRemove: '移除失敗',
      errTargetRequired: '請選擇類別、指定資源、或兩者並用。',
    },
    pasteExtract: {
      buttonLabel: '貼文字自動識別',
      buttonTooltip: '貼上菜單或服務清單，AI 自動擷取成產品資料',
      title: '貼上 & 擷取',
      descriptionPaste: '貼上你的菜單、服務清單或價目表。AI 會擷取成一行行，你審核後再儲存。',
      descriptionReview: '檢查 {count} 項擷取的產品。可以修改或刪除不對的項目。',
      descriptionDone: '完成！',
      placeholders: {
        salon:
          '什麼都可以貼 — 服務清單、價目表。例：\n\n凝膠指甲 — $280 / 90 分鐘\n修甲 — $120 / 45 分鐘\n指甲彩繪加收 — $50',
        restaurant:
          '什麼都可以貼 — 菜單、價目表。例：\n\n招牌炒飯 — $68\n雲吞麵 — $48\n冰檸檬茶 — $28',
        cafe:
          '什麼都可以貼 — 飲品菜單、輕食清單。例：\n\n拿鐵咖啡 — $38\n冰滴咖啡 — $32\n可頌 — $28\n起司蛋糕 — $45',
        retail:
          '什麼都可以貼 — 商品清單、價目表。例：\n\n純棉 T 恤 (S/M/L) — $180\n牛仔外套 — $680\n帆布袋 — $120',
        medical:
          '什麼都可以貼 — 服務清單、診療費。例：\n\n初診 — $600 / 45 分鐘\n回診 — $400 / 30 分鐘\n針灸 — $500',
        fitness:
          '什麼都可以貼 — 課程清單、方案價格。例：\n\n單堂課 — $180 / 60 分鐘\n10 堂方案 — $1,500\n1 對 1 私人訓練 — $800 / 60 分鐘',
        education:
          '什麼都可以貼 — 課程清單、方案價格。例：\n\n一對一教學 — $500 / 60 分鐘\n4 堂方案 — $1,800\n團體班 — $200 / 90 分鐘',
        professional:
          '什麼都可以貼 — 服務清單、收費表。例：\n\n初次諮詢 — $1,200 / 60 分鐘\n一般 engagement — $5,000 起\n月費 retainer — $8,000',
        generic:
          '什麼都可以貼 — 產品或服務清單、價目表。\n每行格式：名稱 — 價錢 — 備註（可選）\n\n例：\n產品 A — $100\n服務 B — $200 / 30 分鐘',
      },
      extract: '擷取',
      extracting: '正在讀取你的 catalog⋯',
      currencyDetected: '偵測到幣別：{currency}',
      colName: '名稱',
      colPrice: '價錢',
      colDescription: '描述',
      colDuration: '時長（分鐘）',
      typeProduct: '產品',
      typeService: '服務',
      addRow: '加一行',
      removeRow: '刪除這行',
      startOver: '重新開始',
      publishNow: '匯入後立即上架 — AI 可即時報價及接受預約。取消勾選則存為草稿。',
      doneDrafts: '{count} 項已存為草稿，上架前 AI 不會報價。',
      importButton: '匯入 {count} 項',
      importing: '匯入中⋯',
      doneSummary: '成功匯入 {count} 項。',
      doneFailed: '{count} 項無法匯入 — 可能超過方案上限。',
      cancel: '取消',
      close: '關閉',
      errorTooShort: '貼多幾項看看，AI 要有內容才能擷取。',
      errorExtractFailed: '這段文字無法擷取。試試減少項目或清晰一點的格式。',
      errorNoItems: '找不到產品。試試清晰一點的格式。',
      errorNetwork: '網路錯誤 — 請再試一次。',
    },
  },

  orders: {
    title: '訂單',
    noOrders: '目前無訂單',
    noOrdersHint: '客戶下單後將會顯示在此處',
    orderNumber: '訂單號',
    customer: '客戶',
    amount: '金額',
    status: '狀態',
    time: '時間',
    actions: '操作',
    markPaid: '標記已付款',
    markPaidConfirm: '確定將此訂單標記為已付款？',
    confirmOrder: '確認訂單',
    confirmOrderConfirm: '確定確認此訂單？客戶會收到通知。',
    completeOrder: '完成訂單',
    completeOrderConfirm: '確定將此訂單標記為完成？客戶會收到通知。',
    markShipped: '標記已出貨',
    markShippedConfirm: '確定將此訂單標記為已出貨？',
    markDelivered: '標記已送達',
    markDeliveredConfirm: '確定將此訂單標記為已送達？',
    cancelOrder: '取消訂單',
    cancelOrderConfirm: '確定取消此訂單？此操作無法復原，庫存會自動恢復。',
    cancelReason: '取消原因（可留空）：',
    pendingConfirm: '待確認',
  },

  customers: {
    title: '客戶',
    searchPlaceholder: '搜尋名稱、電話...',
    allChannels: '全部頻道',
    sortLastSeen: '最近互動',
    sortEngagement: '活躍度',
    sortSpent: '總消費',
    sortOrders: '訂單數',
    sortName: '名稱',
    noCustomers: '目前無客戶記錄',
    noCustomersHint: '請嘗試調整搜尋或篩選條件',
    noCustomersEmpty: '客戶發送訊息後將會顯示在此處',
    customerOverview: '客戶概覽',
    count: '位客戶',
  },

  bookings: {
    title: '預約',
    listTab: '預約列表',
    calendarTab: '日曆',
    availabilityTab: '可預約時間',
    noBookings: '目前無預約',
    noBookingsHint: '客戶透過 WhatsApp 預約後將會顯示在此處',
    selectHint: '選擇一個預約查看詳情',
    titleFnb: '訂位',
    listTabFnb: '訂位列表',
    availabilityTabFnb: '可訂時段',
    noBookingsFnb: '目前無訂位',
    noBookingsHintFnb: '客戶透過 WhatsApp 訂位後將顯示在此',
    selectHintFnb: '選擇一個訂位查看詳情',
    addBookingFnb: '新增訂位',
    today: '今天',
    allDay: '全天',
    upcoming: '即將到來',
    service: '服務',
    duration: '時長',
    durationMin: '分鐘',
    customer: '客戶',
    phone: '電話',
    date: '日期',
    time: '時間',
    price: '價錢',
    notes: '備註',
    editNotes: '編輯備註',
    noNotes: '無備註',
    records: '記錄',
    recordsHint: '為每次預約留下脈絡 — 之後追蹤、同事接手都更輕鬆',
    addRecord: '新增記錄',
    recordPlaceholder: '今日進度 / 客戶反應 / 療程設定 / 下次跟進⋯',
    recordEmpty: '目前沒有記錄',
    saveRecord: '儲存',
    cancelRecord: '取消',
    editRecord: '編輯',
    deleteRecord: '刪除',
    deleteRecordConfirm: '刪除這條記錄？已上傳的附件將一併刪除。',
    attachFile: '加附件',
    attaching: '上傳中⋯',
    attachmentTooLarge: '檔案超過 5MB',
    attachmentRejected: '不支援此檔案類型（僅接受相片與 PDF）',
    attachmentLimit: '每條記錄最多 5 個附件',
    sessionCounter: '第 {n} 次（共 {m} 次）',
    sessionRemaining: '剩 {n} 次',
    deposit: '訂金',
    depositRequired: '需訂金 {pct}%',
    depositPaid: '已收訂金',
    depositPending: '訂金未收',
    cancelReason: '取消原因',
    viewOrder: '查看訂單',
    viewCustomer: '查看客戶',
    viewConversation: '查看對話',
    createdAt: '建立時間',
    updatedAt: '最後更新',
    noShow: '未到',
    confirmBooking: '確認',
    completeBooking: '完成',
    cancelBooking: '取消',
    payment: '付款',
    search: '搜尋客戶名稱或電話⋯',
    staffTab: '服務人員',
    resourcesTab: '場地設備',
    staffTitle: '服務人員管理',
    addStaff: '新增人員',
    editStaff: '編輯人員',
    staffName: '顯示名稱',
    staffNameZh: '中文名稱',
    staffTitleField: '顯示稱呼（選填）',
    staffTitlePlaceholder: '例：Dr. 梁醫師 / 治療師 Stella / Felix sir / 技師 Dave',
    staffTitleLegalHint: '「醫生」、「醫師」、「物理治療師」、「註冊護士」屬法律保護稱號 — 請確保合資格使用。',
    staffFallbackLabel: '負責同事',
    staffColor: '顏色',
    staffServices: '可提供服務',
    staffSchedule: '排班表',
    staffBlockedDates: '休假日',
    noStaff: '目前無服務人員',
    noStaffHint: '新增服務人員以啟用指定人員預約',
    staffActive: '在職',
    staffInactive: '已停用',
    deactivateStaff: '停用',
    activateStaff: '啟用',
    unassigned: '未指派',
    autoAssign: '自動分配',
    assignedTo: '指派給',
    assignedResources: '已安排設備',
    reassign: '重新指派',
    enableStaffMode: '啟用指定服務人員',
    disableStaffMode: '關閉指定服務人員',
    staffModeHint: '啟用後客戶預約時可以選擇指定服務人員',
    resources: {
      title: '場地 / 設備',
      description: '房間、椅子、儀器等資源 — 系統會防止同一時段重複預約。',
      addResource: '新增資源',
      allLocations: '所有分店',
      emptyTitle: '尚無資源',
      emptyDesc: '加入房間、椅子或儀器以追蹤使用狀況。需要特定房間的服務會在預約時自動分配。',
      gatedTitle: '場地 / 設備為商業版功能',
      gatedDesc: '追蹤房間、椅子與儀器，在資料庫層級防止重複預約。適合有共用工作站的美容院、有診間的診所，或需要專用儀器的工作室。',
      gatedBullet1: '資料庫層級的重複預約防護',
      gatedBullet2: '依分店追蹤房間與設備',
      gatedBullet3: '容量上限與清理緩衝時間',
      gatedBullet4: '預約時自動分配資源',
      pitchMedical: '醫美療程一撞機就失信於客戶 — HIFU、光子嫩膚、皮秒雷射只要撞到就難補救。場地設備功能在資料庫層面保證兩位客人不會同時佔用同一台機。',
      pitchSalon: '一間療程室、兩位同事、兩位客人 — 撞預約只是時間問題。場地設備追蹤每間房、每張椅、每台機，AI 助理不會提供做不到的時段。',
      pitchRestaurant: '卡位、包廂、吧檯 — 用場地設備對應到預約，AI 只會 confirm 你實際能服務的時段。',
      pitchGeneric: '保護房間、椅子、設備不被重複預約。AI 助理只會讓客人選擇系統真的能提供的時段。',
      inlineUpsellTitle: '這個服務可能需要指定房間或機器？',
      inlineUpsellBody: '升級 Business 版可以將服務綁定指定房間、椅子或儀器 — 預約時會自動隱藏無法分配的時段。',
      planBadge: 'Business 方案',
      previewLabel: '範例類別',
      viewPlans: '查看方案',
      needLocation: '需要至少一個啟用中的分店才能新增資源。',
      privateLabel: '私人',
      cleanupLabel: '清理：{n} 分鐘',
      edit: '編輯',
      editTitle: '編輯資源',
      addTitle: '新增資源',
      name: '名稱',
      namePlaceholder: '例：1 號房、A 椅、HIFU 機…',
      category: '類別',
      categoryHint: '從你自建的類別中選擇。管理類別請至「類別」設定。',
      manageCategories: '管理類別',
      noCategoriesYet: '尚無類別',
      createCategoryFirst: '請先建立資源類別，才能新增資源。',
      location: '分店',
      selectPlaceholder: '— 請選擇 —',
      capacity: '容量',
      capacityHint: '可同時接待的人數。一般房間填 1，團體教室可填更多。',
      cleanup: '清理時間（分鐘）',
      notes: '備註',
      notesPlaceholder: '內部備註、保養安排等',
      privateCheckbox: '私人 — 客戶指定要私人房時優先分配',
      errName: '請填寫名稱',
      errLocation: '請選擇分店',
      errCategory: '請選擇類別',
      errSaveFailed: '儲存失敗',
      errModuleOff: '進階預約功能未開啟，請先到「設定 → 模組」開啟',
      errPlanLocked: '你的方案未包括房間及儀器',
      errCategoryArchived: '此類別已封存，請先還原或改選其他類別',
      errNotFound: '分店、類別或資源已不存在，請重新載入頁面',
      errDeactivateFailed: '停用失敗',
      deactivateConfirm: '停用「{name}」？',
      cleanupHint: '每次預約後的清潔 / 準備時間，服務結束後資源仍會被佔用相應時間。',
      inUseConfirm: '停用後，此分店將無法預約：{services}。確定停用？',
      futureBookingsNote: '仍有 {n} 個未來預約正在使用此資源，請檢查：{list}',
      outOfService: {
        title: '暫停使用',
        hint: '維修中或需暫停一段時間？標記時段後，AI、網上預約頁及後台在該時段內都不會再提供此資源。設定的時間範圍會套用於每一天。',
        button: '暫停使用',
        none: '尚無已排定的暫停時段。',
        upcoming: '{n} 個即將',
        add: '新增',
        from: '由',
        to: '至',
        startTime: '開始時間',
        endTime: '結束時間',
        timeHint: '兩個時間都留空即表示全日。',
        type: '類型',
        typeMaintenance: '維修保養',
        typeUnavailable: '暫停',
        reason: '原因',
        reasonPlaceholder: '例如：原廠例行保養 + 校準',
        allDay: '全日',
        remove: '移除',
        removeConfirm: '恢復「{name}」{when} 的使用？',
        bookedTitle: '此時段有 {n} 個未來預約正在使用此資源',
        bookedHint: '這些預約會保留時段，不會被取消。仍然儲存後，請改期或聯絡客人。',
        saveAnyway: '仍然儲存',
        savedWithBookings: '已儲存。該時段仍有 {n} 個預約正在使用此資源，請改期或聯絡客人。',
        errDates: '請檢查日期：結束日不可早於開始日，亦不可為過去的日期。',
        errTimes: '請填寫兩個時間，結束要遲過開始。',
        errSpan: '最長一年；更長時間請直接停用資源。',
        errFailed: '儲存失敗',
      },
      cancel: '取消',
      save: '儲存',
      saving: '儲存中…',
    },
    resourceCategories: {
      title: '資源類別',
      resourceCount: '{count} 個資源',
      description: '將資源（治療房、特定儀器、座位等）分類，服務可以引用這些類別決定要預約什麼。',
      addCategory: '新增類別',
      emptyTitle: '尚無類別',
      emptyDesc: '類別是你描述可預約資源的分類 — 例如「治療房」「HIFU 機」「洗髮椅」。加入符合你業務的類別。',
      editTitle: '編輯類別',
      addTitle: '新增類別',
      name: '名稱',
      namePlaceholder: '例：治療房、HIFU 機…',
      nameZh: '名稱（中文）',
      nameZhPlaceholder: '可選的本地化名稱',
      categoryDescription: '描述',
      descriptionPlaceholder: '這個類別用來做什麼？',
      icon: '圖示',
      color: '顏色',
      sortOrder: '排序',
      save: '儲存',
      cancel: '取消',
      saving: '儲存中…',
      delete: '封存',
      deleteConfirm: '封存類別「{name}」？',
      deleteInUse: '無法封存：仍有 {count} 個啟用中資源使用此類別，請先處理。',
      errName: '請填寫名稱',
      errDuplicate: '已有同名的類別',
      errSaveFailed: '儲存失敗',
      errDeleteFailed: '封存失敗',
    },
  },

  inquiries: {
    title: '客戶查詢',
    noInquiries: '目前無查詢',
    noInquiriesHint: 'AI 記錄的客戶查詢將會顯示在此處',
    backToList: '返回列表',
    customerLabel: '客戶：',
    phoneLabel: '電話：',
    createdLabel: '建立時間：',
    resolvedLabel: '解決時間：',
    viewConversation: '查看相關對話 →',
    descriptionLabel: '描述',
    resolutionNote: '解決備註',
    resolutionNotePlaceholder: '解決備註...',
    markInProgress: '標記處理中',
    markResolved: '標記已解決',
    updating: '更新中...',
    reopen: '重新開啟',
    selectInquiry: '選擇一個查詢以查看詳情',
  },

  knowledge: {
    title: '知識庫',
    searchPlaceholder: '搜尋知識庫...',
    noKnowledge: '目前無知識',
    noKnowledgeSearch: '搜尋不到相關內容',
    noKnowledgeHint: '新增常見問題、政策、店鋪資料等，AI 會自動引用來回答客戶。',
    categories: { faq: '常見問題', policy: '政策', info: '資訊', general: '一般' },
    aiKeywords: 'AI 已學習',
    aiGeneratingKeywords: 'AI 學習中⋯',
    aiTraining: '學習中',
    aiReady: '已就緒',
    editKnowledge: '編輯知識',
    addKnowledge: '新增知識',
    editHint: '修改知識內容，AI 會自動更新搜尋關鍵字。',
    addHint: '新增知識後，AI 會自動生成搜尋關鍵字，方便之後搜尋。',
    titleField: '標題',
    titlePlaceholder: '例：退貨政策',
    contentField: '內容',
    contentPlaceholder: 'AI 會根據此段內容回答客戶問題',
    categoryField: '分類',
    updatedAt: '更新時間：',
    selectKnowledge: '選擇一條知識以查看詳情',
    import: '匯入',
    importTitle: '匯入知識',
    importSourceUrl: '網址',
    importSourceFile: '檔案',
    importSourceText: '貼上文字',
    importUrlPlaceholder: '輸入網頁網址，例如 https://myshop.com/faq',
    importTextPlaceholder: '直接貼上文字內容（FAQ、政策、服務說明等）',
    importFileDrop: '拖放檔案或點擊選擇',
    importFileSupported: '支援 PDF、Word、PowerPoint、TXT、圖片',
    importExtracting: '正在提取內容...',
    importExtractingHint: 'AI 正在讀取並整理您的內容',
    importPreviewTitle: '預覽提取內容',
    importPreviewEdit: '如內容有誤，可直接編輯',
    importSplitting: '正在拆分知識...',
    importSplittingHint: 'AI 正在將內容拆分成獨立知識條目',
    importReviewTitle: '確認知識條目',
    importReviewCount: 'AI 已整理出 {count} 條知識',
    importSelectAll: '全選',
    importAsDraft: '匯入為草稿',
    importPublish: '直接發佈',
    importPublishFailed: '有 {n} 條未能儲存，已保留勾選，請再試一次。',
    importLikelyDuplicate: '可能與現有的「{title}」重複 — 已取消勾選，確認不同才發佈。',
    importBack: '返回',
    importNext: '繼續',
    importStart: '開始提取',
    importError: '匯入失敗',
    importNoContent: '未能提取到有效內容',
    tabEntries: '知識條目',
    tabSources: '來源管理',
    sourcesEmpty: '尚無匯入來源',
    sourcesEmptyHint: '匯入檔案或網頁，AI 會自動整理成知識庫',
    sourceEntries: '{count} 條知識',
    sourceProcessing: '處理中...',
    sourceFailed: '處理失敗',
    sourceDeleteTitle: '刪除來源',
    sourceDeleteKeep: '僅刪除來源',
    sourceDeleteAll: '連同知識一併刪除',
    sourceDeleteKeepHint: '保留已產生的知識條目',
    sourceDeleteAllHint: '此來源產生的 {count} 條知識也會被刪除',
    filterBySource: '來源',
    sourceFilterAll: '全部來源',
    sourceFilterManual: '手動新增',
    sourceFilterConversation: '對話提取',
    sourceBadgeManual: '手動',
    sourceBadgeFile: '檔案',
    sourceBadgeUrl: '網頁',
    sourceBadgeConversation: '對話',
    sourceBadgeImage: '圖片',
    sourceBadgeText: '文字',
    entryFromSource: '來源',
    viewSource: '查看來源',
  },


  usage: {
    title: '使用量',
    loadFailed: '載入失敗',
    planNames: { free: '免費', starter: '入門', pro: '專業', business: '商業', enterprise: '企業' },
  },

  plan: {
    limitReachedTitle: '方案未包含此功能',
    scaleCapTitle: '已達上限',
    limitReachedBody: '你的 {plan} 方案未包含 {feature}，升級後立即可用。',
    scaleCapBody: '你的 {plan} 方案在 {feature} 已達上限，升級到 {target} 解鎖更多。',
    upgradeCta: '升級到 {plan}',
    manageOnWeb: '請在瀏覽器開啟 app.novixone.ai 管理你的方案',
    quotaReachedBody: '本月的 {feature} 額度已用完。',
    quotaUsage: '本月已使用 {used} / {limit}',
    unlimited: '無限制',
    creditsExhaustedTitle: 'AI credits 已用完',
    creditsExhaustedBody:
      '本期的 AI credits 已用完，AI 功能暫時停用。購買 credit pack 或升級方案即可繼續使用。',
    creditsExhaustedCta: '前往帳單設定',
    featureNames: {
      monthlyReplies: 'AI 回覆',
      monthlyKnowledgePagesImported: '知識庫匯入頁數',
      maxTeamMembers: '團隊成員',
      maxWhatsappNumbers: 'WhatsApp 號碼',
      maxProducts: '商品',
      maxKnowledgeEntries: '知識庫條目',
      maxCustomerSegments: '客戶分群',
      maxAutomationRules: '自動化規則',
      customRoles: '自訂角色',
      knowledgeAdvancedImport: '進階知識匯入 (PDF / URL / OCR)',
      cohortFunnelAnalytics: '群組 / 漏斗分析',
      conversationalAnalytics: 'AI 問數（對話式分析）',
      integrationEvents: '事件推送（Webhook）',
      publicApiRead: 'API 讀取',
      publicApiWrite: 'API 寫入',
      aiActions: 'AI 動作',
      integrationConnectors: '現成連接',
      providerContract: '連接你自己的系統',
      customDomain: '自訂網域',
    },
  },

  login: {
    heroTitle1: '將客戶對話',
    heroTitle2: '轉化為',
    heroTitle3: '營收',
    heroSubtitle: 'AI 在 WhatsApp、LINE 及網站對話中 24/7 協助您接待客戶、下單、跟進。',
    features: {
      aiSupport: '全渠道 AI 客服',
      aiSupportDesc: '24/7 多語言 AI 自動回覆，協助您接單、回覆查詢、預約',
      orderViaChat: '對話即下單',
      orderViaChatDesc: '客戶在 WhatsApp、LINE 對話中直接下單，無需跳轉',
      memory: '客戶記憶',
      memoryDesc: 'AI 記住每位客戶的偏好，提供個人化服務',
    },
    orEmail: '或使用電子郵件登入',
    emailLabel: '電子郵件地址',
    emailPlaceholder: '',
    checkEmail: '請查看您的電子郵件',
    emailSentTo: '已發送登入連結至',
    clickLink: '請點擊電子郵件中的連結登入後台',
    backToLogin: '← 返回登入',
    signInTitle: '登入後台',
    signInSubtitle: '登入您的 NovixOne 帳號',
    signUpTitle: '建立帳號',
    signUpSubtitle: '建立 NovixOne 帳號，5 分鐘完成設定',
    googleSignIn: '使用 Google 帳號登入',
    googleSignUp: '使用 Google 帳號註冊',
    orEmailSignUp: '或使用電子郵件註冊',
    freePlanBadge: '免費方案：每月 100 位活躍客戶 · 無需信用卡',
    joinTeamTitle: '加入團隊',
    joinTeamSubtitle: '店舖已邀請您使用 NovixOne。建立帳號後，即可接受邀請並開始處理對話、預約及客戶。',
    joinTeamCardSubtitle: '請使用收到邀請的電郵地址，完成後會返回邀請頁。',
    bookDemo: '預約示範',
    worksWith: '支援渠道',
    webChat: '網站對話',
    trustLine: '資料加密 · 私隱設計 · 資料由您掌控',
    passwordHint: '最少 8 個字元',
    recommendedBadge: '推薦',
    welcomeBack: '歡迎回來',
    verifyEmailTitle: '驗證您的電子郵件',
    verifyEmailSentTo: '我們已寄出驗證連結至',
    verifyEmailHint: '請點擊郵件中的連結確認身份，之後即可繼續設定 NovixOne。',
    sending: '發送中…',
    sendLink: '發送登入連結',
    passwordLabel: '密碼',
    passwordPlaceholder: '輸入您的密碼',
    signIn: '登入',
    signUp: '建立帳號',
    signingIn: '登入中…',
    signingUp: '建立帳號中…',
    noAccount: '還沒有帳號？',
    hasAccount: '已有帳號？',
    forgotPassword: '忘記密碼？',
    switchToSignUp: '註冊',
    switchToSignIn: '登入',
    orMagicLink: '或使用魔法連結登入',
    orPassword: '或使用電子郵件和密碼登入',
    forgotPasswordTitle: '重設密碼',
    forgotPasswordSubtitle: '輸入您的電子郵件，我們將寄送重設連結',
    sendResetLink: '發送重設連結',
    sendingReset: '發送中…',
    resetEmailSent: '重設連結已發送',
    resetEmailSentDesc: '請檢查電子郵件中的密碼重設連結',
    backToSignIn: '返回登入',
    resetPasswordTitle: '設定新密碼',
    resetPasswordSubtitle: '請輸入您的新密碼',
    newPasswordLabel: '新密碼',
    newPasswordPlaceholder: '至少 6 個字元',
    confirmPasswordLabel: '確認密碼',
    confirmPasswordPlaceholder: '再次輸入密碼',
    updatePassword: '更新密碼',
    updatingPassword: '更新中…',
    passwordMismatch: '兩次輸入的密碼不一致',
    passwordTooShort: '密碼至少需要 6 個字元',
    passwordResetSuccess: '密碼已更新成功',
    passwordResetRedirect: '正在跳轉到後台…',
    termsAgreement: '建立帳戶即代表您同意我們的',
    legalLinks: { terms: '服務條款', privacy: '私隱政策', aup: '可接受使用政策' },
    trustEncryption: '資料加密',
    trustSecurity: '安全保障',
    trustSetup: '快速設定',
    trustSetupTime: '5 分鐘',
    trustAutoReply: 'AI 自動回覆',
    trustAutoReplyTime: '24/7',
    footer: '© 2026 Novix Star Limited · novixone.ai',
  },

  onboarding: {
    title: 'NovixOne 設定',
    subtitle: '一分鐘內設定你的 AI 助手',
    setupComplete: '設定完成！',
    redirecting: '正在跳轉至控制台...',
    redirectHint: '按照設定清單完成其餘設定',
    settingUp: '正在設定中...',
    step1Title: '商家資料',
    step1Subtitle: '介紹你的事業',
    step2Title: 'AI 助手',
    step2Subtitle: '自訂你的 AI',
    step3Title: '確認並啟動',
    step3Subtitle: '檢查並開始',
    businessNameLabel: '商家名稱',
    businessNamePlaceholder: '例如：明軒家品',
    industryLabel: '產業',
    // 會員計劃步驟（喺 AI 助手之後、確認之前）
    regionLabel: '主要營業地區',
    aiNameLabel: 'AI 助手名稱',
    aiNamePlaceholder: '留空將使用你的商家名稱',
    toneLabel: 'AI 對話風格',
    toneFriendly: '友善',
    toneProfessional: '專業',
    toneCasual: '隨意親切',
    summaryTitle: '設定摘要',
    editButton: '修改',
    next: '下一步',
    back: '上一步',
    launch: '啟動',
    // ── V3 對話式 onboarding ──
    describeTitle: '說說你的生意',
    describeSubtitle: '一兩句話就夠 — 做什麼行業、在哪裡、有什麼特色。',
    describeHint: '沒想法？點一下這些：',
    describeContinue: '繼續',
    describeAnalyzing: '正在理解你的生意⋯',
    exampleChipFnb: '餐飲 / 飲品',
    exampleChipSalon: '美容 / 美甲',
    exampleChipRetail: '零售',
    exampleChipProfessional: '專業服務',
    exampleChipHealth: '健康 / 保健',
    exampleChipEducation: '教育',
    exampleChipOther: '其他',
    reviewTitle: '確認資料',
    reviewSubtitle: '看看我理解得對不對。綠色代表有信心；琥珀色請再確認一下。',
    reviewOriginalLabel: '你說了：',
    reviewReDescribe: '← 重新描述',
    aiInferredBadge: 'AI 分析',
    verifyBadge: '請確認',
    subTypeLabel: '細項',
    subTypePlaceholder: '例如：凝膠指甲 + 零售',
    clarificationHint: '想多問一下：',
    flaggedTitle: '需要我們看一下',
    flaggedBody: '你描述中有些地方需要人工審核才能開通帳戶，我們的同事會盡快聯絡你。',
    extractionFailedHint: '無法自動分析你的描述，請手動填寫下方欄位。',
  },

  activate: {
    heroTitle: '設定你的 AI 助理',
    heroSubtitle: '帳戶已建立。跟著下方步驟設定好，AI 就能好好接待客人，之後連接 WhatsApp 正式上線。',
    progressLabel: '完成 {done} / {total} 步',
    recommendedBadge: '建議',
    doneBadge: '完成',
    catalogTitleCommerce: '加入你的產品',
    catalogTitleBooking: '加入你的服務',
    catalogDescription: '貼上菜單或服務清單。AI 需要這些資料才能報價、接單。',
    catalogDone: '已加入 {count} 項',
    hoursTitle: '設定預約時段',
    hoursDescription: '什麼時間開放客人預約？AI 用這個檢查可用時段。',
    hoursDone: '已設定 {count} 個時段',
    policiesTitle: '設定政策',
    policiesDescription: '取消期限、退換規則、訂金。沒有固定規則可留空 — AI 會直接轉給你處理。',
    paymentTitle: '設定收款',
    paymentDescription: '連接 Stripe，或列出當地付款方式。',
    paymentDone: '收款已設定',
    teachTitle: '一步教會你的 AI',
    teachSubtitle: '貼上你的網站，或常見問題／價目表。我們會整理成 AI 可用的答案，你可以在知識庫檢查。',
    teachUrlTab: '網站連結',
    teachTextTab: '貼上文字',
    teachUrlPlaceholder: 'https://your-shop.com',
    teachTextPlaceholder: '營業時間、價錢、地址、如何預約、付款方式、取消政策⋯',
    teachSubmit: '教會我的 AI',
    teachSubmitting: '讀取中⋯',
    teachStarted: '收到！AI 正在讀取，一兩分鐘內便會在知識庫看到整理好的答案。',
    teachFailed: '無法讀取，請試試直接貼上文字。',
    teachViewKnowledge: '打開知識庫',
    knowledgeTitle: '教 AI 了解你的生意',
    knowledgeDescription: 'FAQ 與店家專屬資料。可以貼上現有素材，或上線後讓 AI 從你的實際回覆中累積。',
    knowledgeDone: '已有 {count} 條知識',
    sandboxTitle: '在測試模式試試',
    sandboxDescription: '扮演客人與 AI 對話 — 至少 3 則訊息才夠深入，找出真正的問題。',
    sandboxProgress: '已測 {count}/{total} 則 — 繼續試一下。',
    sandboxDone: '已測試 — 看看有沒有需要調整？',
    goLiveTitle: '準備上線了？',
    goLiveDescription: '連接 WhatsApp 就能接待真實客人。',
    goLiveWarning: '還有 {count} 個建議步驟未完成 — 仍可上線，但 AI 會較常轉給你處理。',
    goLiveButton: '連接 WhatsApp',
    skipToDashboard: '暫時略過 — 先前往總覽',
    noSellingModulesHint: '訂單及預約均未開啟，因此沒有產品、時段及收款步驟。',
    manageFeaturesLink: '管理功能',
    liveTitle: '你已經上線了！',
    liveSubtitle: '你的 AI 現在正在 WhatsApp 接待真實客戶。',
    liveTestCta: '試著傳一則訊息',
    liveDashboardCta: '前往總覽',
    liveRemainingHint: '還有 {pending} 個步驟未完成 — 每完成一項，AI 轉給你處理的對話就會少一些。',
  },

  getStarted: {
    title: '歡迎來到 NovixOne 👋',
    subtitle: '開始之前，請花一分鐘確認以下條款。我們會在下一步幫您設定商戶帳戶。',
    legalIntro: '本服務由 諾辰科技有限公司（Novix Star Limited）提供。請閱讀並同意以下文件：',
    docs: { tos: '服務條款', privacy: '隱私政策', aup: '可接受使用政策', dpa: '資料處理協議' },
    checkbox1Prefix: '我已閱讀並同意',
    checkbox1Sep: '、',
    checkbox1And: '、',
    checkbox1End: '與',
    authorityInline: '簽署即代表您有權代表您的商戶',
    marketingOptIn: '我想收到 NovixOne 的產品更新、優惠與教學內容',
    marketingOptInHint: '可隨時在設定中取消訂閱',
    optionalLabel: '選填',
    button: '同意並繼續',
    ctaRemaining: '還要勾選 {count} 項',
    submitting: '處理中...',
    error: '出錯了，請再試一次',
    progressLabel: '設定進度',
    progressStep1: '同意條款',
    progressStep2: '商戶資料',
    progressStep3: 'AI 設定',
    progressStep4: '啟動',
    supportHint: '有問題？',
    supportEmail: 'support@novixone.ai',
  },

  legalBanner: {
    title: '我們更新了法律條款',
    desc: '請查看並重新確認最新的服務條款、隱私政策、可接受使用政策與資料處理協議。',
    review: '查看更新',
    accept: '我同意',
    accepting: '處理中...',
    error: '出錯，請再試',
  },

  invite: {
    invalidLink: '邀請連結無效',
    teamInvite: '團隊邀請',
    joinTeam: '加入團隊',
    inviteMessage: '您已被邀請加入 NovixOne 團隊。',
    clickToJoin: '點擊下方按鈕確認加入。',
    accept: '接受邀請',
    joining: '加入中…',
    successJoined: '成功加入{name}團隊！',
    redirecting: '正在跳轉至後台…',
    backToLogin: '返回登入',
    invitedBy: '{inviter} 邀請您加入',
    forEmail: '邀請對象：{email}',
    createAccount: '建立帳號並加入',
    signIn: '已有帳號？登入',
    wrongAccount: '您目前以 {current} 登入，但此邀請是發給 {invitee} 的。請以該電郵登入後再接受。',
    switchAccount: '登出並換帳號',
    errorInvalid: '此邀請已失效或已被撤回，請向店舖索取新的邀請。',
    errorMismatch: '此邀請是發給另一個電郵地址的，請以受邀電郵登入。',
    errorGeneric: '未能加入團隊，請稍後再試。',
  },

  pay: {
    successTitle: '付款成功',
    successMessage: '感謝您的購買！我們已收到您的付款。',
    orderNumber: '訂單編號：',
    successHint: '您可以關閉此頁面，返回 WhatsApp 繼續對話。',
    cancelTitle: '付款已取消',
    cancelMessage: '您的付款尚未完成。如需重新付款，請返回 WhatsApp 聯絡商家。',
    cancelHint: '您可以關閉此頁面。',
  },

  setup: {
    title: '完成設定',
    progress: '{done}/{total} 項完成 · {pct}%',
    addServices: '加入服務項目',
    addProducts: '加入產品／服務',
    productsDone: '已加入 {count} 項',
    productsHint: 'AI 需要了解您的商品才能為客戶推薦',
    paymentSetup: '設定付款方式',
    paymentStripe: 'Stripe 線上收款已連結',
    paymentLocal: '已設定手動付款方式',
    paymentHint: '連結 Stripe 或新增手動付款方式（轉帳、流動錢包、現金等）',
    knowledgeFaq: '填寫常見問題',
    knowledgeBase: '填寫知識庫',
    knowledgeDone: '已加入 {count} 條資料',
    knowledgeHintSupport: '常見問題、使用指南等，讓 AI 回答更加準確',
    knowledgeHint: '常見問題、退換政策等，讓 AI 回答更加準確',
    knowledgeDraftsPending: '有 {count} 則擷取到的草稿等你審核',
    bookingSetup: '設定預約時段',
    bookingDone: '已設定 {count} 個時段',
    bookingHint: '設定開放時間，客戶才能透過 AI 進行預約',
    askCopilot: '使用 AI 助手協助設定',
    sandboxTest: '測試 AI 對話',
    sandboxDone: '已在沙盒測試 AI',
    sandboxHint: '上線前以客戶身份測試您的 AI',
    segmentSetup: '設定客戶分群',
    segmentHint: '依行為將客戶分類，可針對性進行行銷',
    segmentCount: (n) => `已建立 ${n} 個分群`,
    outreachSetup: '設定主動行銷',
    outreachDone: '行銷模板已就緒',
    outreachHint: '為棄單與流失客戶設定自動跟進',
  },

  sandbox: {
    title: 'AI 沙盒',
    subtitle: '以客戶身份測試您的 AI 助手。這裡的資料與正式環境完全隔離。',
    inputPlaceholder: '以客戶身份輸入訊息...',
    newConversation: '新對話',
    clearAll: '清除所有測試資料',
    clearConfirm: '將會刪除所有沙盒測試資料，包括對話、訂單、預約、查詢及客戶記錄，確定繼續？',
    cleared: '所有測試資料已清除',
    creditsRemaining: '剩餘 {n} Credits',
    creditsExhausted: 'AI Credit 餘額已用完，充值後可繼續測試。',
    noMessages: '發送訊息開始測試您的 AI',
    banner: '沙盒模式 — 這裡的對話與資料不會影響正式環境',
    history: '測試記錄',
  },

  push: {
    title: '開啟推送通知',
    description: '當客戶需要人工接手時即時通知你，不會錯過任何對話。',
    allow: '允許通知',
    dismiss: '關閉',
    integrationApprovalTitle: '需要批准：{action}',
  },

  copilot: {
    title: 'AI 助手',
    greeting: '有什麼可以幫助您？',
    clearChat: '清除對話',
    welcomeTitle: 'AI 助手',
    welcomeHint: '您可以使用自然語言管理業務，例如：「新增服務『冷氣清洗』，$480」、「今天有多少預約？」，或上傳產品清單 / 價目表圖片',
    uploadImage: '上傳圖片',
    pendingUpload: '待上傳',
    analyzeImage: '請分析這張圖片',
    imageInputPlaceholder: '描述圖片或直接傳送…',
    inputPlaceholder: '輸入訊息…',
    morningBriefing: '給我今日的 briefing',
    actionNotDoneYet: '尚未執行任何操作。',
    actionConfirm: '確認',
    actionCancel: '取消',
    actionChallengeMismatch: '輸入不相符，請核對數字後再試。',
    actionNotAllowed: '您已沒有此權限，或相關功能已關閉。',
    actionError: '未能完成，請再試一次。',
    uploadFailed: '上載失敗。圖片須小於 4 MB。',
    workingSteps: '已完成 {n} 步',
    continueRun: '繼續',
    batchApprove: '批准所選的 {n} 項',
    batchCancelAll: '全部取消',
    batchTitle: '{n} 項待確認',
    batchFinished: '已完成 {ok} 項，{failed} 項未成功。',
    batchDeclined: '已全部取消，沒有執行任何操作。',
    creditsUsed: '已用 {n} credits',
    stop: '停止',
    actionTainted: 'AI 是在讀取他人撰寫的內容（例如客戶訊息或檔案）後提出此操作，請確認這是您想要的。',
    fileRejected: '無法使用此檔案。支援 25 MB 以內的 Excel 或 CSV。',
    importThisFile: '請匯入這個檔案中的產品。',
  },

  settings: {
    title: '設定',
    locations: {
      title: '分店',
      description: '管理你的分店 / 門市，每間分店有自己的營業時間、員工、時區。',
      defaultBadge: '預設',
      additionalBranches: '其他分店',
      multiBranchLockedTitle: '多分店需要 Pro 或以上計劃',
      multiBranchLockedBody: 'Pro 計劃最多可管理 3 間分店，Business 計劃最多 10 間，包括每間分店的營業時間、員工排班及設備。',
      branchLimitReachedTitle: '已達分店上限',
      branchLimitReachedBody: '升級到 Enterprise 解鎖無限分店，或者先刪除一間。',
      viewPlans: '查看計劃',
      addBranch: '新增分店',
      services: '服務',
      edit: '編輯',
    },
    channels: {
      title: '頻道',
      description:
        '已連接的訊息頻道 — WhatsApp 號碼及 LINE 帳號。管理每條頻道的名稱、AI 員工及預設發送號碼。',
      emptyState: '尚未連接任何頻道。',
      emptyStateHint: '連接 WhatsApp 號碼或 LINE 官方帳號，讓 AI 開始回覆你的客戶。',
      connectChannel: '連接頻道',
      connectWhatsApp: '連接 WhatsApp',
      connectLine: '連接 LINE',
      chooseChannelTitle: '選擇要連接的頻道',
      whatsappOptionHint: '以 Meta 登入連接 WhatsApp Business 號碼',
      lineOptionHint: '需要 LINE 官方帳號（免費），約 10 分鐘，附逐步教學',
      activeBadge: '已連接',
      sandboxBadge: '沙盒',
      defaultBadge: '預設',
      defaultHint: '群發及其他非對話內的訊息會由預設頻道發出。',
      setDefault: '設為預設',
      connectedOn: '連接於 {date}',
      editLabel: '編輯名稱',
      labelPlaceholder: '例如：主店、預約專線',
      agentLabel: 'AI 員工',
      agentInherit: '商戶預設',
      disconnect: '解除連接',
      disconnecting: '解除中⋯',
      disconnectTitle: '解除連接 {name}？',
      disconnectBody: 'AI 將立即停止在此頻道回覆。對話紀錄會保留，你可以隨時重新連接。',
      disconnectDefaultNote: '此頻道是你的預設頻道 — 解除後，最早連接的其餘頻道會自動成為新預設。',
      loadError: '無法載入頻道。',
      waTitle: '連接 WhatsApp Business',
      waIntro: '你將以 Facebook 登入，並在彈出視窗中選擇（或建立）WhatsApp Business 號碼。',
      waPrereq: '請準備：Meta Business 帳號，以及一個未註冊到其他 WhatsApp 帳號的電話號碼。',
      waContinue: '以 Facebook 繼續',
      waWaiting: '等待 Facebook⋯',
      waWaitingHint: '請在 Facebook 視窗完成步驟，完成後會自動關閉。',
      waCancelled: 'Facebook 授權已取消。',
      waSuccessTitle: '號碼已連接',
      waSuccessBody: '{number} 已連接，AI 會立即開始回覆此號碼的客戶。',
      waConnectAnother: '連接另一個號碼',
      error: '儲存失敗，請重試。',
    },
    templates: {
      title: 'WhatsApp 訊息模板',
      description:
        '用於群發、自動跟進及系統通知（預約確認、提醒）的訊息模板。模板須經 Meta 審核通過後才能發送。',
      manageLink: '管理 WhatsApp 訊息模板',
      sync: '同步狀態',
      syncing: '同步中…',
      syncDone: '已從 Meta 同步 {n} 個模板',
      syncFailed: '同步失敗，請重試。',
      loadError: '無法載入模板。',
      emptyNotConnected: '請先連接 WhatsApp 號碼',
      emptyNotConnectedCta: '前往頻道設定',
      emptyNoTemplates: '尚未有任何模板。',
      emptyNoTemplatesHint: '按「同步狀態」向 Meta 註冊標準模板並取得審核狀態。',
      systemGroup: '系統模板',
      customGroup: '自訂模板',
      statusApproved: '已通過',
      statusPending: '審核中',
      statusPendingHint: '通常幾分鐘至 24 小時',
      statusRejected: '被拒絕',
      statusPaused: '已暫停',
      statusPausedHint: '因質量評分過低被 Meta 暫停，會自動恢復',
      statusDisabled: '已停用',
      qualityLabel: '質量',
      qualityGreen: '良好',
      qualityYellow: '一般',
      qualityRed: '欠佳',
      categoryUtility: '交易通知',
      categoryUtilityHint: '訂單／預約相關，24 小時對話期內免費。',
      categoryMarketing: '推廣訊息',
      categoryMarketingHint: '優惠及宣傳內容，按條收費。',
      bodyLabel: '訊息內容',
      variablesLabel: '變數',
      languageLabel: '語言',
      wabaLabel: 'WhatsApp Business 帳戶',
      lastSynced: '最後同步 {date}',
      rejectedReason: '拒絕原因',
      viewDetail: '查看詳情',
      detailMetaName: 'Meta 模板名稱',
      newTemplate: '新增模板',
      editTemplate: '修改',
      systemBadge: '系統通知',
      systemEditHint:
        '此為平台預設文案。一旦你修改，此模板將不再跟隨系統日後的更新。',
      editTemplateLang: '修改{language}版',
      pickerNoApproved: '尚無已通過審核的模板。請先建立模板並等待 Meta 審核通過。',
      pickerAutoFilled: '按收件人自動填入',
      pickerLanguageAuto: '語言會按每位客人的偏好自動選擇',
      langNames: {
        zh_HK: '繁體中文',
        zh_CN: '簡體中文',
        en: '英文',
      },
      addTranslation: '新增翻譯',
      variantAdded: '{language}版本已提交審核',
      variantAddFailed: '未能新增翻譯，請重試。',
      languageExists: '此模板已有該語言版本。',
      otherLanguages: '其他語言',
      searchLanguage: '搜尋語言…',
      noLanguageResults: '找不到相符語言',
      operatingLanguages: '營運語言',
      operatingLanguagesHint:
        '你為客人提供服務的語言。模板覆蓋率、翻譯建議及預設發送均依照此清單 — 第一個為主要語言。',
      primaryLang: '主要',
      makePrimary: '設為主要語言',
      addLanguage: '新增語言',
      removeLangTitle: '移除{language}？',
      removeLangBody: '此語言已審批的模板會保留、仍可發送 — 只是不再計入覆蓋率。',
      provisionSystemCheckbox: '同時一鍵將所有系統模板的{language}版本提交予 Meta 審核（建議）',
      provisionNonRegistryHint:
        '系統模板可在每張模板卡逐一新增此語言版本 — AI 翻譯會起草內容供你審閱。',
      insightTitle: '語言洞察',
      insightLine: '有 {n} 位客人偏好{language}，你暫未支援此語言。',
      insightAdd: '加入{language}',
      insightDismiss: '略過',
      bulkFillLine: '{n} 個系統模板已備有{language}官方文案，可提交 Meta 審核。',
      bulkFillCta: '檢視並提交',
      bulkFillProgress: '提交中 {i}/{n}…',
      bulkFillDone: '已提交 {n} 個模板予 Meta 審核。',
      bulkFillPartial: '已提交 {ok} 個，{fail} 個失敗 — 可在模板卡逐個重試。',
      bulkFillNonRegistryHint:
        '{n} 個系統模板尚無{language}版本 — 請在各模板卡逐一新增（AI 翻譯會起草內容）。',
      bulkReviewTitle: '提交 {n} 個{language}系統模板',
      bulkReviewNote:
        '以上為系統官方文案，提交後由 Meta 審核（一般數分鐘至數小時），批准後對應功能自動生效。',
      bulkReviewConfirm: '提交 {n} 個予 Meta 審核',
      initializing: '系統模板初始化中，正在你的 WhatsApp 帳戶建立…',
      ghostTemplatesNote: '另有 {n} 個模板屬於已斷開的 WhatsApp 號碼，未在此顯示。',
      provisionAfterConnectHint: '連接 WhatsApp 後，系統模板會自動以你的營運語言提交審核。',
      authExpired: 'WhatsApp 授權已失效，模板無法同步或發送 — 請重新連接你的 WhatsApp 帳戶。',
      detailPreviewLabel: '客人會收到的訊息',
      technicalDetails: '技術資料',
      qualityGood: '良好',
      qualityFair: '一般',
      qualityPoor: '欠佳',
      lastSyncedLabel: '最後同步',
      pickerNoMarketing: '你尚未有已審批的推廣模板 — 群發必須使用「推廣」類別的模板才可發送。',
      customEmptyTitle: '你的推廣訊息模板',
      customEmptyBody:
        '客人超過 24 小時沒有回覆後，WhatsApp 只允許發送經 Meta 審批的模板。群發、自動化流程及 AI 助手跟進都會從這裡取用模板 — 立即建立你自己的推廣內容。',
      addVariantTitle: '新增{language}版本',
      addVariantReviewNote: '提交後會將此內容送交 Meta 審核（一般數分鐘至數小時），批准後自動生效。',
      addVariantSubmit: '提交予 Meta 審核',
      addVariantGoEditor: '前往編輯器',
      addVariantEditorHint: '此版本需在編輯器撰寫 — AI 翻譯會根據現有版本起草內容，經你審閱後才提交。',
      manageLanguages: '管理',
      removeLangAction: '移除',
      system: {
        booking_confirmation: {
          title: '預約確認通知',
          trigger: '客人成功預約後立即自動發送',
        },
        appointment_reminder_24h: {
          title: '預約前一日提醒',
          trigger: '預約前 24 小時自動發送',
        },
        appointment_reminder_2h: {
          title: '預約前兩小時提醒',
          trigger: '預約前 2 小時自動發送',
        },
        order_confirmed: {
          title: '訂單確認同付款指示',
          trigger: '客人下單後立即自動發送',
        },
        payment_received: {
          title: '收到付款通知',
          trigger: '你確認收到付款後自動發送',
        },
        payment_reminder: {
          title: '付款提醒',
          trigger: '預付訂單 4 小時後仍未付款時自動發送（只在你當地時間 09:00–21:00 發送）',
        },
        order_cancelled: {
          title: '訂單取消通知',
          trigger: '訂單被取消時自動發送',
        },
        review_request: {
          title: '邀請客人留評價',
          trigger: '完成預約或訂單後自動發送',
        },
        booking_slot_available: {
          title: '候補有位通知',
          trigger: '有人取消、空出候補客人想要的時段時，由你發出',
        },
        product_back_in_stock: {
          title: '貨品到貨通知',
          trigger: '客人等候的貨品到貨時自動發送',
        },
      },
      groups: {
        booking: '預約通知',
        orders: '訂單同付款',
        relationship: '客戶關係',
        custom: '你撰寫的訊息',
        customHint: '群發、自動化流程及 AI 跟進均從此發送。',
        customTrigger: '由你自行發送 — 群發、自動化流程、AI 跟進',
        notUsedByYourBusiness: '{group} — {n} 個暫不適用於你的業務',
      },
      state: {
        working: '運作中',
        workingLang: '運作中（{languages}）',
        inReview: 'Meta 審核中 · 一般數分鐘至 24 小時',
        fallbackWarn: '{missing}客人會收到{fallback}',
        fallbackFix: '加{language}版',
        cannotSend: '無法發送 — 尚未有任何語言獲批',
        cannotSendFix: '立即設定',
        rejected: '被 Meta 拒絕 — 需要修改',
        rejectedFix: '修改並重新提交',
      },
      attention: {
        title: '{n} 個通知需要處理',
        filterAll: '全部（{n}）',
        filterNeedsAction: '需要處理（{n}）',
        allClear: '所有通知都運作中',
      },
      cost: {
        free: '免費',
        freeHint:
          '訂單、預約相關通知。客人 24 小時內回過訊息就免費。',
        paid: '每則收費',
        paidHint: '推廣內容。Meta 會按發送數量收費。',
      },
      intro: {
        title: '為何 WhatsApp 要事先審批訊息？',
        body1:
          '客人超過 24 小時沒有回覆後，WhatsApp 只允許你使用事先經 Meta 審批的內容聯絡對方。',
        body2:
          '此處列出的就是這些內容。每個只需審批一次（一般數分鐘至 24 小時），之後可持續發送。',
        body3: '訂單同預約通知免費；推廣訊息每則收費。',
        dismiss: '明白了',
      },
      searchPlaceholder: '搜尋通知…',
      noSearchResults: '沒有符合的通知',
      editor: {
        newTitle: '新增 WhatsApp 模板',
        editTitle: '修改模板',
        displayName: '模板名稱',
        displayNamePlaceholder: '例如：生日優惠通知',
        categoryLabel: '用途',
        bodyPlaceholder: '輸入訊息內容。請用下方按鈕插入變數，不需手動輸入 {{1}}。',
        insertVariable: '插入變數',
        customVariable: '自訂…',
        customVariablePrompt: '變數名稱（小寫英文字母／底線，例如 coupon_code）',
        footerLabel: '頁尾（可選）',
        footerPlaceholder: '例如：回覆「停止」即可取消接收',
        buttonsLabel: '快速回覆按鈕（可選）',
        buttonsHint: '最多 3 個按鈕，每個 25 字以內。',
        addButton: '新增按鈕',
        samplesTitle: '變數示例值',
        samplesHint: '供 Meta 審核人員參考，並用於預覽。',
        previewTitle: '預覽',
        submit: '提交審核',
        submitting: '提交中…',
        resubmit: '儲存並重新提交審核',
        reviewNote: 'Meta 審核通常需時幾分鐘至 24 小時，結果會以通知告知你。',
        editQuotaNote: '已通過的模板每 24 小時只可修改一次（每 30 日最多 10 次）。審核期間舊版本會繼續生效。',
        pendingLocked: '此模板審核中，須待 Meta 有結果後才能修改。',
        deleteTemplate: '刪除模板',
        deleteConfirmTitle: '確定刪除此模板？',
        deleteConfirmBody: '模板會同時從 Meta 移除，使用此模板的群發及自動化將無法發送。',
        metaRejected: 'Meta 拒絕了提交：{message}',
        unknownVariables: '系統模板只可使用內置變數：{allowed}',
        submitted: '已提交審核',
        variantTitle: '新增翻譯',
        variantSubtitle: '{key} · {language}',
        variantNote: '翻譯版本沿用原模板的變數，只需改寫文字內容。每種語言由 Meta 獨立審核。',
        translateFrom: '✨ AI 由{language}翻譯',
        moreLanguages: '更多語言…',
        translating: '翻譯中…',
        aiDraftTitle: 'AI 幫我寫',
        aiDraftHint: '用一句話說明你想發送的內容，AI 會起草一個合規的推廣模板，經你審閱修改後才提交。',
        aiDraftPlaceholder: '例如：新店開張全單 8 折，叫客人回覆預約',
        aiDraftCta: '起草',
        aiDraftLoading: '起草中…',
        aiDraftFailed: 'AI 起草失敗，請再試一次或者自行撰寫。',
        translateFailed: 'AI 翻譯失敗，請手動輸入或重試。',
        variablesMismatch: '變數必須與原模板一致：{expected}',
        sectionBasics: '基本資料',
        sectionContent: '訊息內容',
        sectionChecklist: '提交前檢查',
        startTitle: '想如何開始？',
        startHint: '之後所有內容都可以修改——按下提交之前，不會送交 Meta。',
        startAi: 'AI 幫我寫',
        startAiHint: '用一句話說明你想發送的內容',
        startBlank: '自己寫',
        startBlankHint: '由空白訊息開始',
        startCopy: '複製現有模板',
        startCopyHint: '從你已有的模板開始',
        copySourceTitle: '要複製哪個模板？',
        copySourceEmpty: '暫時未有可複製的模板。',
        aiRewrite: '用 AI 重寫',
        aiOverwriteTitle: '要覆蓋你已撰寫的內容？',
        aiOverwriteBody: 'AI 草稿會取代現有的訊息、頁尾同按鈕。完成後可以還原一次。',
        aiOverwriteConfirm: '覆蓋',
        aiDraftBadge: 'AI 草稿 · 請檢查',
        aiDraftUndo: '還原',
        metaNamePreview: 'Meta 名稱：{name}',
        metaNameLocked: '提交後不可更改。',
        metaNameFallback: 'Meta 名稱只接受英文字母，所以會變成「{name}」。建議加幾個英文字，方便日後辨認。',
        headerLabel: '標題（選填）',
        headerHint: '訊息上方一行粗體短句。標題不支援變數。',
        headerNone: '不使用',
        headerText: '文字',
        headerPlaceholder: '例如：你的預約已確認',
        buttonsHintV2: '最多 3 個快速回覆，或者 1 個網址 + 1 個致電按鈕。WhatsApp 不容許兩種混用。',
        buttonTypeQuickReply: '快速回覆',
        buttonTypeUrl: '網址',
        buttonTypePhone: '致電',
        buttonLabelPlaceholder: '按鈕文字',
        buttonUrlPlaceholder: 'https://example.com/book',
        buttonPhonePlaceholder: '+85298765432',
        removeButton: '移除按鈕',
        variableGroupCustomer: '顧客',
        variableGroupCommerce: '訂單',
        variableGroupBooking: '預約',
        variableGroupOther: '其他',
        customVariableTitle: '自訂變數',
        customVariableLabel: '變數名稱',
        customVariableInvalid: '只可用細楷英文、數字同底線，並以英文字母開頭——例如 coupon_code。',
        customVariableAdd: '插入',
        samplesInlineHint:
          'Meta 審核員會看到這些示例值，預覽亦會使用它們。留空則使用預設示例 — 實際發送時會按每位客人代入真實內容。',
        checklistPassed: '已通過 {n} 項',
        checklistErrors: '{n} 項須修正',
        checklistWarnings: '{n} 項建議',
        checklistAllGood: '全部檢查通過，可以提交。',
        checklistFix: '前往修正',
        submitBlocked: '尚有 {n} 項需要修正',
        previewShowSamples: '示例值',
        previewShowVariables: '變數名稱',
        previewBillingMarketing: '推廣模板——每則發送計費。',
        previewBillingUtility: '交易通知——24 小時對話窗內免費。',
        previewCompareSource: '原文（{language}）',
        rejectionTitle: 'Meta 拒絕了此模板',
        rejectionReasonLabel: 'Meta 給出的原因',
        rejectionFixTitle: '常見修正方法',
        pendingTitle: 'Meta 審核中',
        backToList: '返回模板列表',
        draftRestoreTitle: '要復原未提交的草稿？',
        draftRestoreBody: '你上次離開時仍有未提交的修改。可以復原，或者重新開始。',
        draftRestore: '復原草稿',
        draftDiscard: '重新開始',
        draftSaved: '草稿已自動儲存於此裝置',
        leaveTitle: '未提交就離開？',
        leaveBody: '此模板尚未提交予 Meta。草稿會保留在此裝置。',
        leaveStay: '繼續編輯',
        leaveConfirm: '離開',
        lint: {
          bodyEmpty: '訊息內容不能為空。',
          bodyTooLong: '內容超過 1,024 字上限。',
          startsWithVariable: 'Meta 會拒絕以變數開頭的模板——請在前面加上文字。',
          endsWithVariable: 'Meta 會拒絕以變數結尾的模板——請在後面加上文字。',
          adjacentVariables: '兩個變數不能相連——請在中間加上文字。',
          variableDensity: '變數比例過高的訊息較易被拒——建議加入更多固定文字。',
          headerTooLong: '標題超過 60 字上限。',
          headerHasVariable: '標題必須是固定文字——請將變數移到訊息內容。',
          footerTooLong: '頁尾超過 60 字上限。',
          buttonTooLong: '按鈕文字上限為 25 字。',
          buttonTextEmpty: '每個按鈕都需要文字。',
          tooManyButtons: '快速回覆按鈕最多 3 個。',
          tooManyCtaButtons: '最多只可有一個網址按鈕同一個致電按鈕。',
          mixedButtonTypes: '快速回覆不能與網址／致電按鈕混用——請只選一種。',
          duplicateButtonText: '有兩個按鈕文字相同——Meta 要求每個按鈕文字不同。',
          buttonUrlInvalid: '請輸入完整網址，以 https:// 開頭。',
          buttonPhoneInvalid: '請連國家區號一併輸入，例如 +85298765432。',
          missingSamples: '每個變數都需要提供示例值。',
          marketingNoOptOut: '推廣模板加入取消接收提示（例如「回覆停止即可取消」）較易通過審核。',
          categoryMismatch: '此內容似乎是推廣訊息，但類別選擇了交易通知——Meta 經常因此拒絕。建議改為推廣，或收斂用字。',
        },
        rejection: {
          invalidFormat: '模板格式無效——通常是變數開頭／結尾、變數相連，或變數與示例值不符。',
          tagContentMismatch: '內容與所選類別不符——例如交易通知類別出現推廣字眼。請更改類別或修改內容。',
          abusiveContent: 'Meta 判定內容違反其政策，請檢視用字。',
          scam: 'Meta 懷疑內容涉及詐騙。請移除催逼字眼及不清晰的優惠描述。',
          generic: '請對照 WhatsApp 模板政策檢查內容，修改後重新提交。',
        },
        presetVars: {
          customer_name: '顧客名稱',
          business_name: '商戶名稱',
          order_number: '訂單編號',
          service_name: '服務名稱',
          product_name: '商品名稱',
          date: '日期',
          time: '時間',
          amount: '金額',
          reference: '參考編號',
          review_url: '評價連結',
          payment_instructions: '付款方式',
          reason: '原因',
        },
      },
    },
    payment: {
      onlinePlatforms: '線上收款平台',
      onlinePlatformsDesc: '連結收款平台後，客戶在網店或與 AI 對話下單時，都會自動生成付款連結',
      stripeDesc: '信用卡、Apple Pay、Google Pay',
      stripePayments: '支援信用卡、Apple Pay、Google Pay 等',
      airwallexDesc: '支援多幣種收款，適合跨境商家',
      manualMethods: '手動付款方式',
      manualMethodsDesc: '客戶以這些方式離線付款。確認訂單後，系統會自動向客戶發送付款指引。',
      methodInstructionsPlaceholder: '付款指引（例如：帳號、轉帳詳情）',
      addMethod: '新增付款方式',
      manualHint: '您需要自行確認收款並更新訂單狀態。',
      vacationMode: '休假模式',
      vacationModeDesc: '開啟後，AI 不接受新訂單或預約',
      returnDate: '預計返回日期',
      vacationMessage: '給客戶的留言',
      vacationMessagePlaceholder: '例如：我們週一回來！',
      flowTitle: '收款流程',
      flowDesc: '下單時如何處理付款',
      flowStep1: 'AI 為客戶下單後，若已連結線上收款平台，會自動生成付款連結發送給客戶。',
      flowStep2: '客戶付款後，訂單自動更新為已付款，同時通知商家。',
      flowStep3: '若未連結線上平台，客戶會看到您設定的手動付款方式，並依照指引付款。商家需自行確認收款並更新訂單狀態。',
      stripeConnected: 'Stripe 已連結',
      stripeSetupIncomplete: 'Stripe 設定未完成',
      stripeEnabled: '已啟用',
      stripeSetup: '設定中',
      stripeVerified: '✓ 帳戶已驗證',
      stripeVerifying: '⚠ 帳戶驗證中',
      stripeChargesEnabled: '✓ 可接收付款',
      stripeChargesDisabled: '⚠ 收款功能未啟用',
      stripePayoutsEnabled: '✓ 自動提款已開啟',
      stripePayoutsHint: '提款需至帳戶設定',
      stripeContinue: '繼續設定',
      stripeDisconnect: '斷開連結',
      stripeDisconnecting: '斷開中...',
      stripeDisconnectConfirm: '確定要斷開 Stripe 連結嗎？斷開後客戶將無法透過線上付款連結進行付款。',
      stripeConnect: '連結',
      stripeConnecting: '連結中...',
      stripeDashboard: '管理 Stripe',
      stripeCollapse: '收起管理面板',
      stripeLoadingPanel: '載入 Stripe 面板...',
      stripeLoadError: '無法載入 Stripe 面板',
      stripeMissingKey: 'Stripe publishable key 未設定',
      stripeTabs: { payments: '交易記錄', payouts: '提款', balances: '餘額', account: '帳戶設定' },
    },
    team: {
      title: '團隊',
      desc: '管理團隊成員及權限',
      inviteTitle: '邀請成員',
      invitePlaceholder: '電子郵件地址',
      invite: '邀請',
      inviting: '邀請中...',
      pendingInvites: '待接受邀請',
      expires: '到期：',
      copyLink: '複製連結',
      copied: '已複製',
      removeConfirm: '確定要移除此成員嗎？',
      revokeConfirm: '確定要撤銷此邀請嗎？',
      you: '您',
    },
    notifications: {
      inviteLinkHint: '將此連結發送給對方，對方登入後即可自動加入您的團隊。',
      inviteLinkExpiry: '邀請後會產生連結，您可以{copyLink}發送給對方。邀請有效期 7 日。',
    },
    whatsapp: {
      title: 'WhatsApp 連結',
      desc: '連結您的 WhatsApp Business 帳戶，AI 客服會自動回覆客戶訊息',
      connected: 'WhatsApp 已連結',
      phoneNumber: '電話號碼：',
      displayName: '顯示名稱：',
      linkedAt: '連結時間：',
      features: 'AI 客服功能：',
      featuresItems: {
        autoReply: '自動回覆客戶 WhatsApp 訊息',
        productOrders: '處理產品查詢及下單',
        bookingManagement: '預約管理',
        mediaUnderstanding: '圖片及語音訊息理解',
      },
      disconnect: '斷開 WhatsApp',
      disconnecting: '斷開中...',
      disconnectConfirm: '確定要斷開 WhatsApp 連結嗎？斷開後 AI 客服將無法收發 WhatsApp 訊息。',
      connectTitle: '連結 WhatsApp Business',
      steps: {
        step1: '步驟 1',
        step1Desc: '點擊下方按鈕開始連結',
        step2: '步驟 2',
        step2Desc: '登入您的 Facebook 帳戶並選擇 WhatsApp Business 帳戶',
        step3: '步驟 3',
        step3Desc: '確認連結後，AI 客服會自動開始運作',
      },
      connectButton: '連結 WhatsApp',
      fbSuccess: 'Facebook 授權成功！',
      fbSuccessDesc: '請輸入您的 WhatsApp Business Account ID 及 Phone Number ID 完成設定。您可以在 developers.facebook.com → 您的 App → WhatsApp → API Setup 找到這些資料。',
      wabaId: 'WhatsApp Business Account ID',
      phoneNumberId: 'Phone Number ID',
      verifying: '驗證中...',
      completeSetup: '完成設定',
      metaNotConfigured: 'Meta App ID 未設定，請聯絡管理員。',
      fbNotLoaded: 'Facebook SDK 未載入，請重新整理頁面。',
      setupFailed: '設定失敗，請重試。',
      errors: {
        TOKEN_EXCHANGE_FAILED: '無法完成 Facebook 授權，請重新嘗試連接。',
        SIGNUP_FAILED: 'WhatsApp 連接失敗，請重試。',
        NO_PENDING_TOKEN: '找不到 Facebook 授權，請先完成「連接 WhatsApp」步驟。',
        WABA_VERIFY_FAILED: '無法驗證 WABA ID。請確認 ID 正確，並且你已完成 Facebook 授權。',
        PHONE_NOT_IN_WABA: 'Phone Number ID 不屬於此 WABA。可用的電話號碼：{availablePhones}',
        NUMBER_IN_USE: '此號碼已連接到另一個 NovixOne 帳戶。請先在該帳戶中斷開連接，或聯絡客服。',
        WABA_NO_PHONES: '此 WABA 下找不到任何電話號碼。請先於 Meta Business Suite 新增電話號碼。',
      },
    },
    billing: {
      title: '方案與帳單',
      desc: '管理您的訂閱方案',
      loading: '載入中...',
      loadFailed: '載入失敗',
      currentPlan: '目前方案',
      manage: '管理訂閱',
      used: '已使用',
      resets: '下次重設：',
      choosePlan: '選擇方案',
      popular: '推薦',
      current: '目前方案',
      currentPlan2: '目前方案',
      perMonth: '月',
      processing: '處理中...',
      upgrade: '立即升級',
      downgrade: '降級',
      enterpriseDesc: '無限 AI credits、專屬客戶經理、自訂整合',
      contactUs: '聯絡我們',
      subscriptionSuccess: '訂閱成功！您的方案已更新。',
      credits: {
        title: 'AI Credits',
        desc: '一個額度通用所有 AI 功能 — AI 回覆、Copilot、知識庫、洞察都由此扣除。',
        totalRemaining: '剩餘 credits',
        monthlyGrant: '每月方案 credits',
        purchasedBalance: '加購 credits（永不過期）',
        replyAnchor: '1 個 AI 回覆 ≈ {n} credits',
        burnTitle: '用量 — 最近 {days} 天',
        burnEmpty: '此期間尚未有 AI 用量。',
        burnSummary: '過去 {days} 日已使用 {count} credits',
        unit: 'credits',
        groupLabels: {
          customer_ai: '客服 AI',
          copilot: 'Copilot',
          knowledge: '知識庫',
          insights: '洞察',
          drafts: '草稿',
          embeddings: '搜尋',
          other: '其他',
        },
        packsTitle: '購買 Credits',
        packsDesc: '一次性購買，永不過期。',
        packCredits: '{n} credits',
        packBuy: '購買',
        purchaseSuccess: 'Credits 購買成功！已加入您的餘額。',
        // Usage & limits (mobile parity with /usage — RFC usage-revamp Phase 5)
        forecastUnlimited: '無上限方案，毋須預測。',
        forecastInsufficient: '數據未夠 3 日，暫時無法預測。',
        forecastOk: '按此速度，期末約會使用方案的 {pct}%。',
        forecastTight: '按此速度，期末將使用方案的 {pct}%，建議留意。',
        forecastRunout: '按此速度，所有 credits 將於 {date}（{days} 日後）用完。',
        forecastPace: '最近 7 日平均每日 {n} credits',
        daysLeft: '本期尚餘 {n} 日',
        macLabel: '本月活躍客戶',
        macNoSnapshot: '首次統計後顯示',
        limitsTitle: '方案限額',
        limitsSubtitle: '與 paywall 使用同一組數字',
        limitsShow: '顯示限額',
        limitsHide: '收起',
        limitsUnlimited: '無限制',
        limitsNotMeasured: '未量度',
        limitsNotEnforced: '未生效',
        limitsAtCap: '已達上限',
        limitLabels: {
          monthlyActiveCustomers: '本月活躍客戶',
          maxTeamMembers: '團隊成員',
          maxWhatsappNumbers: 'WhatsApp 號碼',
          maxProducts: '商品 / 服務',
          maxKnowledgeEntries: '知識庫條目',
          maxCustomerSegments: '客戶分群',
          maxAutomationRules: '自動化規則',
          maxLocations: '分店',
          maxCouponCampaigns: '優惠券活動',
          maxCustomFieldsPerObject: '自訂欄位（每個對象）',
          webChatMaxOrigins: '網站聊天網域',
          monthlyWaTemplateBroadcasts: '本月 WhatsApp 群發',
          monthlyEmailBroadcasts: '本月電郵群發',
          monthlyAutomationExecutions: '本月自動化執行',
          maxCouponCodesPerBatch: '每批優惠碼上限',
          mediaStorageMb: '媒體儲存空間',
          mediaRetentionDays: '媒體保留期',
          auditRetentionDays: '稽核紀錄保留期',
        },
      },
      planBullets: {
        free: [
          '每月 50K AI credits',
          '1 個 WhatsApp 號碼',
          '最多 2 位團隊成員',
          '無限商品、50 條知識庫（可由網站 / PDF 匯入）',
          '基本客戶 CRM + 訂單管理',
          '手機 App + 0% Stripe Connect 手續費',
        ],
        starter: [
          '每月 250K AI credits',
          '1 個 WhatsApp 號碼 · 5 位團隊成員',
          '無限商品、500 條知識庫',
          'WhatsApp 群發每月 500 位收件人',
          '電郵群發每月 500 位收件人',
          '包含所有 Free 方案功能',
        ],
        pro: [
          '每月 1M AI credits',
          '3 個 WhatsApp 號碼 · 15 位團隊成員',
          '最多 3 間分店、5,000 條知識庫',
          '群組 / 漏斗分析',
          'WhatsApp 群發每月 5,000 位收件人',
          '包含所有 Starter 方案功能',
        ],
        business: [
          '每月 4M AI credits',
          '10 個 WhatsApp 號碼 · 50 位團隊成員',
          '最多 10 間分店、無限知識庫',
          '自訂角色 + 權限管理 · 自訂網域',
          '365 日稽核紀錄',
          '包含所有 Pro 方案功能',
        ],
        enterprise: [
          '無限 AI credits · 無限團隊成員',
          '用量上限及稽核保留期按合約訂定',
          '資料處理協議 (DPA)',
          '專屬客戶經理 + SLA',
          '可協商獨立部署',
        ],
      },
    },
  },

  mobile: {
    tabs: {
      home: '首頁',
      conversations: '對話',
      customers: '客戶',
      business: '業務',
      more: '設定',
    },
    datetime: {
      justNow: '剛剛',
      today: '今天',
      tomorrow: '明天',
      yesterday: '昨天',
    },
    settings: {
      closeAccount: {
        title: '結束帳戶',
        intro: '結束後店舖即時停用，寬限期過後永久刪除所有客戶、對話、訂單同預約。稽核紀錄會為合規保留。',
        graceHint: '{days} 日寬限期 — 期內隨時可以取消。',
        confirmLabel: '輸入店名（{name}）確認',
        confirmPlaceholder: '店名',
        button: '結束帳戶',
        working: '處理中…',
        failed: '無法開始結束程序',
        nameMismatch: '店名不符。',
        confirmAlertTitle: '確定結束帳戶？',
        confirmAlertBody: '所有同事將即時無法再使用此店。寬限期內可以取消。',
        confirmAlertCancel: '保留帳戶',
        confirmAlertConfirm: '結束帳戶',
        pendingTitle: '已排定結束 — 資料將於 {date} 刪除',
        pendingBody: '店舖已停用。取消即可重新啟用並保留所有資料。',
        cancelButton: '取消結束',
        cancelling: '取消中…',
        cancelFailed: '取消失敗',
        ownerOnly: '只有帳戶擁有人可以結束帳戶。',
      },
      region: {
        title: '地區',
        save: '儲存',
        saveError: '儲存失敗',
        unknownError: '未知錯誤',
        introHeading: '你主要在哪裡做生意？',
        introBody: '這會影響電話區號預設、付款方式建議與未來地區化的功能。修改不會影響時區或語言。',
        tapToChange: '點擊變更',
        unsavedNotice: '尚未儲存：將由「{from}」變更為「{to}」',
      },
      profile: {
        title: '個人資料',
        save: '儲存',
        saveError: '儲存失敗',
        unknownError: '未知錯誤',
        introHeading: '只屬於你的帳戶',
        introBody: '這頁的設定只影響你自己的登入體驗，不會影響商家或其他同事。',
        displayNameLabel: '顯示名稱',
        displayNamePlaceholder: '例如：陳小明',
        displayNameHint: '通知、系統記錄與團隊畫面都會看到你的名字。',
        emailLabel: '登入信箱',
        emailSignedOut: '尚未登入',
        emailHint: '信箱是登入身份，目前不支援在 app 中修改。',
      },
      timezone: {
        title: '時區',
        save: '儲存',
        saveError: '儲存失敗',
        unknownError: '未知錯誤',
        introHeading: '選擇你做生意的時區',
        introBody: '影響「今日」「本週」的分界、預約提醒與自動跟進的排程。',
        currentSelection: '當前選擇',
        searchPlaceholder: '搜尋城市或 UTC 偏移',
        emptyResults: '沒有搜尋結果',
        regions: {
          asia: '亞洲',
          pacific: '太平洋',
          europe: '歐洲',
          americas: '美洲',
          africa: '非洲',
          middleEast: '中東',
        },
      },
      account: {
        title: '帳戶資料',
        save: '儲存',
        saveError: '儲存失敗',
        unknownError: '未知錯誤',
        phoneInvalidTitle: '電話格式錯誤',
        phoneInvalidBody: '請檢查電話號碼。',
        introHeading: '你的生意身份',
        introBody: '這些資料會出現在收據、AI 自動回覆與客戶看到的地方。寫得越清楚，AI 越能用你的口吻。',
        nameLabel: '商家名稱',
        namePlaceholder: '例如：晴峰冷氣工程',
        nameHint: '會出現在收據與 AI 介紹自己時。',
        typeLabel: '生意類型',
        typePlaceholder: '例如：物理治療中心、家品零售、室內設計',
        descLabel: '店家簡介',
        descLabelHint: '一兩句話介紹你的業務',
        descPlaceholder: '例如：在銅鑼灣及荃灣設有門市的家品零售店，亦經網店及 WhatsApp 接單。',
        phoneLabel: '聯絡電話',
        phoneHint: '你自己的電話 — AI 轉介客戶時會聯絡你。與客戶的 WhatsApp 號碼無關。',
        nameRequired: '商家名稱不可留空。',
      },
      notifications: {
        title: '通知偏好',
        loadError: '暫時無法載入通知設定',
        introHeading: '選擇你想收到通知的方式',
        introBody: '這些是你個人的設定 — 只影響你自己如何收到通知，與其他同事或商家設定無關。',
        footnote: 'Email 通知可能會延遲幾分鐘。',
        events: {
          escalation: {
            title: '需要接手',
            description: 'AI 處理不了、客戶堅持找人，第一時間通知你。',
          },
          newOrder: {
            title: '新訂單',
            description: '新訂單時通知（不論透過網店、AI 對話或會員中心）。',
          },
          appointment: {
            title: '預約提醒',
            description: '新預約、改期、24 小時 / 2 小時前提醒。',
          },
          inquiry: {
            title: '客戶詢問',
            description: '投訴、問題或新詢問收到時提醒你。',
          },
          followUp: {
            title: '跟進到期',
            description: '你安排的跟進到期或逾期時提醒你。',
          },
          inventory: {
            title: '庫存偏低',
            description: '每日彙報跌至低庫存線或以下的貨品。',
          },
          integrationApproval: {
            title: '待批准操作',
            description: 'AI 想在你自己的系統執行操作，需要同事批准。只會通知有權批准的人，靜音時段亦會照常通知。',
          },
          channel: {
            title: 'WhatsApp 連接狀態',
            description: '當 Meta 中斷您其中一個 WhatsApp 號碼，或號碼恢復連接時通知您。號碼中斷期間，AI 無法回覆該號碼的客人。靜音時段亦會照常通知。',
          },
          dailyReport: {
            title: '每日摘要',
            description: '每日下午 8 點收一次今日生意的小結。',
          },
        },
        channels: {
          webPush: '推送',
          email: 'Email',
        },
      },
      hours: {
        title: '營業時間',
        save: '儲存',
        saveError: '儲存失敗',
        unknownError: '未知錯誤',
        loadError: '載入失敗',
        timeFormatTitle: '時間格式錯誤',
        timeFormatBody: '{day} 的時間要是 HH:MM 格式（例如 09:00）',
        timeOrderTitle: '時間錯誤',
        timeOrderBody: '{day} 的結束時間要晚於開始時間',
        introHeading: '設定每日營業時間',
        introBody: '營業時間會用於網店、預約系統及 AI 回覆 — 例如非營業時間的自動回覆，或提示下單截止時間。',
        days: {
          monday: { label: '星期一', short: '一' },
          tuesday: { label: '星期二', short: '二' },
          wednesday: { label: '星期三', short: '三' },
          thursday: { label: '星期四', short: '四' },
          friday: { label: '星期五', short: '五' },
          saturday: { label: '星期六', short: '六' },
          sunday: { label: '星期日', short: '日' },
        },
        closed: '休息',
        toSeparator: '至',
        timezoneNote: '時區：{tz}',
      },
      categories: {
        title: '商品分類',
        introHeading: '商品分類',
        introBody: '用分類整理你的商品與服務，方便管理與指派人員。',
        emptyTitle: '尚無分類',
        emptySub: '新增分類幫你整理商品，例如「美甲」、「護理」、「按摩」。',
        deactivated: '已停用',
        failed: '失敗',
        sheetTitleCreate: '新增分類',
        sheetTitleEdit: '編輯分類',
        nameLabel: '名稱',
        namePlaceholder: '例如：美甲 / 按摩 / 護理',
        descLabel: '描述',
        descPlaceholder: '選填 — 簡短描述這個分類',
        saveCreate: '建立分類',
        saveEdit: '儲存變更',
        deleteBtn: '刪除分類',
        deleteConfirmTitle: '刪除分類？',
        deleteConfirmBody: '刪除「{name}」— 裡面的商品不會被刪除，只是不再歸類。',
        deleteConfirmCancel: '取消',
        deleteConfirmAction: '刪除',
      },
      outreach: {
        title: '自動跟進',
        loadError: '暫時無法載入跟進設定',
        introHeading: '讓 AI 主動幫你聯絡客戶',
        introBody: '開啟後，AI 會在合適的時間自動發送 WhatsApp 訊息跟進 — 你不需要再記住誰該 follow up。',
        footnote: 'WhatsApp 規定：自動跟進訊息要使用 Meta 已批准的模板。開啟這裡之後，還要在網頁版「自動跟進」確認模板設定好才會真正發送。',
        triggers: {
          abandonedInterest: {
            title: '詢問未下單',
            tagline: '隔日輕推一下',
            description: '客戶問過價格、看過產品但未下單 — AI 會隔一天用你批准的訊息模板再 follow up 一次，減少流失。',
            cadence: '觸發後 24 小時內 · 每位客戶最多 7 天 1 次',
          },
          lapsedCustomer: {
            title: '老客戶安靜了',
            tagline: '主動 Win-back',
            description: '購買過的客戶 14 天沒動靜 — AI 會提醒你記住他們，自動發一個溫暖的問候訊息，試著帶回他們做下一單。',
            cadence: '安靜 14 天後 · 每位客戶最多 30 天 1 次',
          },
          postOrder: {
            title: '下單之後',
            tagline: '完成後再問候',
            description: '訂單完成或預約結束 72 小時後，AI 會自動問候客戶，確認一切順利 — 同時邀請好評，建立長期關係。',
            cadence: '完成後 72 小時 · 每位客戶最多 14 天 1 次',
          },
        },
      },
      policies: {
        title: '政策',
        save: '儲存',
        saveError: '儲存失敗',
        saveSuccessTitle: '已儲存',
        saveSuccessBody: '政策已更新。',
        unknownError: '未知錯誤',
        sectionBooking: '預約取消政策',
        sectionOrder: '訂單取消政策',
        sectionVacation: '休假模式',
        allowCancelBooking: '允許客戶取消預約',
        allowCancelOrder: '允許客戶取消訂單',
        windowLabel: '取消期限',
        windowBookingOption: '{hours} 小時前',
        windowOrderOption: '{hours} 小時內',
        windowBookingHint: '客戶要在預約前幾小時取消才有退款',
        windowOrderHint: '客戶下單後幾小時內可以取消',
        refundLabel: '退款方式',
        refundFull: '全額退款',
        refundPartial: '部分退款',
        refundNone: '不退款',
        refundPercentLabel: '退款比例',
        blockIfShipped: '已出貨後不可取消',
        vacationToggle: '啟用休假模式',
        vacationHint: '開啟後 AI 不會處理新訂單或預約，只會回覆你設定的訊息。',
        vacationReturnDate: '回歸日期',
        vacationMessage: '休假訊息',
        vacationMessagePlaceholder: '例如：我們放假中，1月3號回來，到時見！',
        notConfigured: '尚未設定取消政策',
        notConfiguredHint: 'AI 會把所有取消要求轉給你人工處理。設定政策後 AI 可自動處理符合條件的取消。',
        configureButton: '設定政策',
        clearButton: '清除政策',
      },
      soul: {
        title: 'AI 個性',
        save: '儲存',
        saveError: '儲存失敗',
        unknownError: '未知錯誤',
        loadError: '載入失敗',
        introHeading: '教 AI 像你一樣',
        introBody: '挑一個語氣，新增幾條一定要做的守則與絕不能做的紅線。AI 每次回客都會跟著做。',
        sectionTone: '語氣',
        sectionGuidelines: '守則',
        sectionRedLines: '紅線',
        guidelinesHint: 'AI 一定要做的事，例如：「永遠先問客戶名」、「報價要含運費」',
        redLinesHint: 'AI 絕對不能做的事，例如：「不要隨便打折」、「不要回答醫療問題」',
        guidelinePlaceholder: '新增一條守則',
        redLinePlaceholder: '新增一條紅線',
        guidelinesEmptyTitle: '尚無守則',
        guidelinesEmptySub: '新增一條教 AI 如何接客',
        redLinesEmptyTitle: '尚無紅線',
        redLinesEmptySub: '新增一條保護你的品牌',
        tones: {
          friendly: { label: '親切', sub: '溫暖、近距離' },
          professional: { label: '專業', sub: '正式、有禮' },
          casual: { label: '輕鬆', sub: '隨意、像朋友' },
          enthusiastic: { label: '熱情', sub: '活潑、有活力' },
        },
      },
      availability: {
        title: '可預約時段',
        saveSettings: '儲存設定',
        saveSchedule: '儲存時段',
        saveError: '儲存失敗',
        unknownError: '未知錯誤',
        settingsSavedTitle: '已儲存',
        settingsSavedBody: '預約設定已更新。',
        scheduleSavedTitle: '已儲存',
        scheduleSavedBody: '每週時段已更新，新時段會即時套用於網店、AI 預約及人手新增。',
        blockFailedTitle: '新增失敗',
        sectionSettings: '預約設定',
        sectionWeekly: '每週時段',
        sectionBlocked: '封鎖日期',
        staffModeLabel: '服務人員分派',
        staffModes: {
          none: { label: '不需要', sub: '使用全域容量，不分派人員' },
          auto: { label: '自動分派', sub: '系統自動分派服務人員' },
          choice: { label: '客戶可以挑選', sub: 'AI 會問客戶想找哪位' },
        },
        bufferLabel: '預約間隔（分鐘）',
        bufferHint: '每個預約之間的緩衝時間',
        maxDaysLabel: '最遠可預約天數',
        maxDaysHint: '客戶最多可以預約幾天之後',
        weeklyHint: 'AI 只會在這些時段內幫客戶預約。「容量」即是同一時間最多可以接幾位客。',
        capacityLabel: '容量',
        capacitySuffix: '位客',
        addRange: '新增時段',
        blockedHint: '假期或特殊休息日 — 這些日子 AI 不會接受預約。',
        blockDatePlaceholder: '日期 (YYYY-MM-DD)',
        blockReasonPlaceholder: '原因（選填）',
        dayLabels: {
          sun: '週日',
          mon: '週一',
          tue: '週二',
          wed: '週三',
          thu: '週四',
          fri: '週五',
          sat: '週六',
        },
      },
      payment: {
        title: '付款方式',
        save: '儲存',
        saveError: '儲存失敗',
        unknownError: '未知錯誤',
        loadError: '載入失敗',
        deleteConfirmTitle: '刪除這個付款方式？',
        deleteConfirmBody: '刪除後無法復原。',
        deleteConfirmCancel: '取消',
        deleteConfirmAction: '刪除',
        nameEmptyTitle: '名稱不可留空',
        nameEmptyBody: '第 {index} 個付款方式要有名稱，例如「銀行轉帳」',
        introHeading: '告訴客戶如何付款',
        introBody: '新增幾個付款方式，例如銀行轉帳、PayPal、Wise。客戶在網店結帳或與 AI 下單時都會看到。',
        emptyTitle: '尚無付款方式',
        emptyHint: '點擊下方新增第一個',
        addBtn: '新增付款方式',
        cardIndexLabel: '第 {index} 個',
        statusActive: '已啟用',
        statusInactive: '已停用',
        namePlaceholder: '名稱，例如：銀行轉帳',
        instructionsPlaceholder: '詳情，例如：台新銀行 123-456789 / 戶名：ABC Studio',
      },
      stripeConnect: {
        title: '信用卡收款',
        introHeading: '讓顧客用信用卡付款',
        introBody: '連結 Stripe 之後，AI 幫顧客下單時會自動發送一個安全的付款連結 — 支援信用卡、Apple Pay、Google Pay。',
        statusNotConnected: '未連結',
        statusSetupIncomplete: '設定未完成',
        statusConnected: '已連結',
        statusChargesDisabled: '未啟用收款',
        statusPayoutsDisabled: '提款審核中',
        badgeVerified: '已驗證',
        badgePending: '待審核',
        connectBtn: '設定 Stripe',
        continueBtn: '繼續完成設定',
        manageBtn: '更新 Stripe 資料',
        disconnectBtn: '斷開 Stripe',
        disconnectConfirmTitle: '斷開 Stripe？',
        disconnectConfirmBody: '斷開後，顧客將無法以信用卡付款，直到你重新連結為止。',
        disconnectConfirmCancel: '取消',
        disconnectConfirmAction: '斷開',
        disconnectError: '目前無法斷開連結',
        loadError: '無法載入 Stripe 狀態',
        adminOnly: '僅店主（admin）可設定 Stripe。',
        sessionMissing: '請先登出再重新登入以更新 session，然後重試。',
        feesNote: 'Stripe 會針對每筆交易收取自身手續費。NovixOne 不會另外加收任何費用。',
        webTitle: 'Stripe 設定',
        webClose: '關閉',
        webLoading: '正在載入 Stripe…',
        webError: '目前無法載入 Stripe，請檢查網路後再試。',
        webRetry: '重試',
      },
      billing: {
        title: '訂閱與用量',
        loadError: '暫時無法載入帳單資訊',
        currentPlanLabel: '你目前的方案',
        usageLabel: '本月 AI 回覆用量',
        unlimited: '無上限',
        enterpriseHint: '你的方案沒有用量上限。有問題請聯絡支援。',
        remainingLabel: '還有 {count} 次回覆',
        dangerWarning: '你快用完本月的額度 — 用完後 AI 會暫停自動回覆。考慮升級方案或購買加購回覆包。',
        warnWarning: '已使用超過八成，注意月底前的用量。',
        topupHeading: '加購回覆包',
        topupWithBalance: '你還有 {count} 次加購回覆，會在月結額度用完後自動扣。',
        topupWithoutBalance: '尚未購買加購回覆包。加購次數可以跨月使用，用完月結額度會自動扣。可以在電腦版購買。',
        upgradeBtn: '升級方案',
        manageBtn: '管理訂閱',
        portalHint: '訂閱管理、付款方式、發票與取消都在 Stripe 的安全頁面處理。',
        openBrowserFailedTitle: '無法開啟網頁',
        openBrowserFailedBody: '請稍後再試。',
        plans: {
          free: { label: '免費', sub: '基本試用 — 足夠開始' },
          starter: { label: '入門', sub: '單人工作室' },
          pro: { label: '專業', sub: '穩定生意流量' },
          business: { label: '商業', sub: '多客戶高頻次' },
          enterprise: { label: '企業', sub: '自訂用量' },
        },
      },
      apps: {
        title: '功能',
        intro: '開啟你的店舖實際提供的功能。關閉的功能會從選單、AI 及網店隱藏 — 不會刪除任何資料。',
        modulesHeading: '功能',
        focusHeading: '主力',
        focusHint: 'AI 主動推薦哪一項、首頁優先顯示哪一項。只影響呈現方式。',
        focusNone: '客服優先',
        focusNoneHint: 'AI 負責解答問題，不會主動推廣訂單或預約。',
        readOnly: '只有擁有人同經理可以更改功能。',
        lockedNote: '你目前的方案不包含此功能。請在 app.novixone.ai 管理方案。',
        requiresOneOf: '需先開啟 {modules}。',
        requiresAll: '需先開啟 {modules}。',
        or: '或',
        notReadyOrders: '已開啟，但尚無上架產品 — AI 暫時無法接單。',
        notReadyBookings: '已開啟，但尚無可預約服務 — AI 暫時無法為客人預約。',
        confirmOffTitle: '關閉「{module}」？',
        confirmOffBody: '{impact}會保留並照常完成，但客人及 AI 無法再新增。',
        impactOrders: '{count} 張未完成訂單',
        impactBookings: '{count} 個未來預約',
        impactDeals: '{count} 個進行中商機',
        confirmOffCancel: '取消',
        confirmOffAction: '關閉',
        cascaded: '一併關閉：{modules}',
        saveError: '儲存失敗',
        loadError: '載入功能失敗。',
        retry: '重試',
      },
      shop: {
        title: '商家設定',
        fallbackShop: '商家',
        businessTypeMissing: '尚未設定生意類型',
        allShopHint: '全店共用 · 變更會影響整個生意',
        pauseAlertTitle: '暫停 AI 自動回覆？',
        pauseAlertBody: '新訊息會直接等你親自回覆，暫停期間不會扣 AI quota。',
        pauseAlertCancel: '取消',
        pauseAlertConfirm: '暫停 AI',
        aiPausedTitle: 'AI 已暫停',
        aiPausedHint: '新訊息會等你接手',
        aiActiveTitle: 'AI 自動回覆',
        aiActiveHint: 'AI 會幫你自動處理對話',
        planHeading: '{plan}方案',
        usageLoading: '載入中…',
        usageLoaded: '已用 {used} / {total} 次回覆',
        unlimited: '無上限',
        usageWarnPercent: '用量已達 {percent}%',
        usageDangerPercent: '用量已達 {percent}% — 即將見底',
        sectionBusiness: '生意',
        sectionAI: 'AI 助手',
        sectionLocale: '地區與語言',
        rowHours: { title: '營業時間', sub: '每日開放時段' },
        rowPayment: { title: '付款方式', sub: '銀行轉帳、PayPal、Wise 等' },
        rowStripe: { title: '信用卡收款', sub: '信用卡、Apple Pay、Google Pay（透過 Stripe）' },
        rowAvailability: { title: '可預約時段', sub: '每週開放時間、封鎖日期' },
        rowPolicies: { title: '政策', sub: '取消規則、退款、休假模式' },
        rowStaff: { title: '服務人員', sub: '團隊成員、服務指派' },
        rowCategories: { title: '商品分類', sub: '整理商品與服務的分類' },
        rowSoul: { title: 'AI 個性', sub: '語氣、守則、紅線' },
        rowOutreach: { title: '自動跟進', sub: '詢問未下單、老客戶、下單後' },
        rowTemplates: { title: 'WhatsApp 訊息模板', sub: '查看模板審核狀態' },
        rowKnowledge: { title: '知識庫', sub: 'FAQ、政策、常見回答' },
        rowAgents: { title: 'AI 代理', sub: '代理人格、技能、觸發詞' },
        rowRegion: '地區',
        rowTimezone: '時區',
        groups: {
          account: '帳戶',
          shop: '店舖',
          operations: '營運',
          messaging: '客戶訊息',
          advanced: '進階',
          upgrade: '升級可解鎖',
        },
        rows: {
          account: { title: '店舖帳戶', sub: '店名、業務類型、簡介' },
          business: { title: '店舖資料', sub: '地區、時區、語言' },
          apps: { title: '功能', sub: '訂單、預約、商機 — 開定關' },
          operations: { title: '營運政策', sub: '自動確認、取消政策、假期模式' },
          scheduling: { title: '預約時段', sub: '每週時段、封鎖日期、更表' },
          payment: { title: '收款方式', sub: 'Stripe、FPS、PayMe、轉帳、現金' },
          templates: { title: '訊息模板', sub: 'WhatsApp 模板審批狀態' },
          marketing: { title: '自動跟進', sub: '放棄興趣、流失客、下單後' },
          billing: { title: '方案與帳單', sub: '方案、發票、AI credits' },
          data: { title: '資料與私隱', sub: '結束帳戶' },
        },
        planSince: '{plan} 起',
        webOnlyHint: '團隊、頻道、自訂欄位及整合請在網頁後台管理。',
        rowLanguage: 'AI 回覆語言',
        languageLabels: {
          yue: '廣東話',
          zhHant: '繁體中文',
          zhHans: '简体中文',
          en: 'English',
        },
        planLabels: {
          free: '免費',
          starter: '入門',
          pro: '專業',
          business: '商業',
          enterprise: '企業',
        },
      },
    },
    languageScreen: {
      title: '語言',
      saveError: '儲存失敗',
      unknownError: '未知錯誤',
      heading: 'AI 與應用程式語言',
      hint: '控制 AI 回覆客戶的語言，以及應用程式介面語言。與地區設定無關 — 您可以在台灣做生意但選擇英文，適合服務國外客戶。',
      languageLabels: {
        zhHant: { label: '繁體中文', sub: '港、台、海外華人通用' },
        zhHans: { label: '简体中文', sub: '中國大陸、新加坡通用' },
        en: { label: 'English', sub: 'International default' },
      },
    },
    shared: {
      loadFailed: '載入失敗',
      retry: '重試',
    },
    voice: {
      recognitionFailed: '語音辨識失敗',
      permissionDenied: '需要麥克風與語音辨識權限',
    },
    more: {
      title: '設定',
      fallbackUser: '使用者',
      fallbackShop: '商店',
      sectionPersonal: '個人設定',
      sectionShop: '商店',
      profile: '個人資料',
      profileSub: '顯示名稱、登入信箱',
      notifications: '通知偏好',
      notificationsSub: '哪些事件、哪個管道要通知你',
      language: '應用程式語言',
      languageSub: '僅影響你裝置上的介面，不影響 AI 回覆',
      shopSettings: '商店設定',
      shopSettingsSub: '{shop} · AI、營業時間、帳單…',
      shopSettingsHint: '商店設定會影響整個生意與所有同事',
      sectionData: '資料與私隱',
      closeAccount: '結束帳戶',
      closeAccountSub: '停用店舖，寬限期後刪除所有資料',
      signOut: '登出',
      support: '說明與支援',
      supportSub: '聯絡 NovixOne 團隊、查看您的請求',
    },
    helpDesk: {
      title: '支援',
      subtitle: '您向 NovixOne 支援團隊提出的請求。',
      newRequest: '新請求',
      empty: '暫時沒有支援請求',
      emptyHint: '聯絡支援後，對話會在這裡顯示。',
      loadError: '無法載入支援請求。',
      retry: '重試',
      category: '與甚麼有關？',
      categories: { billing: '帳單及方案', whatsapp: 'WhatsApp', line: 'LINE', ai: 'AI 回覆', bookings: '預約', orders: '訂單及付款', account: '帳戶及登入', other: '其他' },
      subject: '主旨',
      subjectPlaceholder: '例如：WhatsApp 客人收不到回覆',
      description: '發生了甚麼事？',
      descriptionPlaceholder: '您做了甚麼、預期會怎樣、實際發生了甚麼？',
      contact: '最方便的聯絡方法（選填）',
      contactPlaceholder: '例如：WhatsApp +852 …，或電郵',
      attachedNote: '會隨請求一併傳送：應用程式版本、手機平台及您的角色，有助我們找出問題。',
      send: '傳送請求',
      sending: '傳送中…',
      sent: '已傳送請求 {ref}，我們會在此處及以電郵回覆。',
      sendFailed: '請求未能傳送，請再試一次。',
      subjectTooShort: '主旨最少需要 3 個字。',
      descriptionTooShort: '請簡單描述遇到的問題。',
      statuses: { open: '處理中', pending_merchant: '等待您回覆', pending_internal: '工程團隊跟進中', resolved: '已解決', closed: '已關閉' },
      waitingForYou: '支援已回覆，等待您回應',
      updated: '更新於',
      you: '您',
      supportName: 'NovixOne 支援',
      replyPlaceholder: '輸入回覆…',
      replySend: '傳送',
      replyFailed: '回覆未能傳送。',
      markSolved: '標示為已解決',
      markSolvedConfirm: '將此請求標示為已解決？',
      markSolvedBody: '7 日內仍可回覆以重新開啟。',
      cancel: '取消',
      solvedDone: '已標示為已解決。',
      closedNote: '此請求已關閉。如問題再次出現，請提出新請求。',
      resolvedNote: '此請求已解決。如仍有問題，請於 7 日內回覆。',
      csatQuestion: '這次支援對您有幫助嗎？',
      csatGood: '有幫助',
      csatBad: '沒有幫助',
      csatThanks: '多謝您的意見。',
      slaNote: '支援團隊於辦公時間（星期一至五 09:00–18:00，香港時間）回覆。',
      notFound: '找不到此支援請求。',
      announcementSeverity: { info: '通知', warning: '重要', critical: '緊急' },
      announcementKinds: { incident: '服務問題', maintenance: '系統維護', feature: '新功能', billing: '帳單' },
      dismiss: '關閉',
      learnMore: '詳情',
      update: '最新消息',
    },
    couponCheck: {
      title: '核對優惠券',
      subtitle: '客戶出示優惠碼時即時核對 — 不會佔用名額。',
      inputPlaceholder: '輸入優惠碼，例：SUMMER100',
      checkBtn: '核對',
      checking: '核對中…',
      validTitle: '可以使用 ✓',
      invalidTitle: '不能使用',
      campaignLabel: '優惠活動',
      discountLabel: '折扣：{amount}',
      subtotalCaveat: '以 {amount} 訂單估算 — 實際金額以結帳時計算為準。',
      minSpendLine: '最低消費 {amount}',
      expiresLine: '{date} 截止',
      nextStepHint: '如需核銷：開單時輸入此優惠碼 — 折扣會自動套用並佔用名額。',
      errorGeneric: '核對失敗 — 請再試。',
    },
    business: {
      title: '業務',
      pillarOrders: '訂單',
      pillarBookings: '預約',
      pillarInquiries: '詢問',
      pillarProducts: '商品',
    },
    businessSheets: {
      booking: {
        title: '建立預約',
        save: '儲存',
        unknownError: '未知錯誤',
        fieldCustomer: '客戶',
        fieldCustomerPlaceholder: '選擇客戶或新增',
        fieldService: '服務',
        fieldServicePlaceholder: '選擇服務',
        usePackage: '用套票「{name}」（剩 {left} 次）',
        usePackageHint: '扣一次套票，無需另行收費',
        serviceValue: '{duration} · {price}',
        fieldDate: '日期',
        fieldTime: '時間',
        fieldNotes: '備註',
        notesPlaceholder: '客戶需求、特別安排…',
        hourLabel: '小時',
        minuteLabel: '分鐘',
        pickServiceTitle: '選擇服務',
        noServicesTitle: '尚無服務',
        noServicesHint: '到「商品」新增一個類型為「服務」的項目',
        serviceMinutes: '{minutes} 分鐘',
        errors: {
          noCustomerTitle: '尚未選擇客戶',
          noCustomerBody: '請先選擇一個客戶',
          noServiceTitle: '尚未選擇服務',
          noServiceBody: '請先選擇一項服務',
          timeTitle: '時間錯誤',
          timeBody: '請選擇有效的日期與時間',
          createFailedTitle: '建立失敗',
          slotTitle: '這個時段無法預約',
          slotBody: '仍要建立預約嗎？',
          bookAnyway: '仍要建立',
          cancel: '取消',
          resourceWarningTitle: '預約未分配房間',
          resourceWarningBody: '這個預約沒有空的房間或儀器，請手動安排。',
        },
      },
      inquiry: {
        title: '建立詢問',
        save: '儲存',
        unknownError: '未知錯誤',
        introTitle: '即時記錄詢問',
        introBody: '接到電話 / 客戶到場說的內容，記錄下來方便跟進，不會漏掉。',
        fieldCustomer: '客戶',
        fieldCustomerPlaceholder: '選擇客戶或新增',
        fieldType: '類型',
        fieldPriority: '優先',
        fieldSubject: '主題',
        fieldDescription: '詳細內容',
        subjectPlaceholder: '一句話說明狀況',
        descriptionPlaceholder: '客戶說了什麼、想要什麼、預期如何跟進…',
        types: {
          inquiry: { label: '詢問', sub: '一般問題' },
          complaint: { label: '投訴', sub: '需要跟進' },
          feedback: { label: '意見', sub: '反饋 / 建議' },
          support: { label: '支援', sub: '技術 / 產品支援' },
        },
        priorities: {
          low: '低',
          normal: '普通',
          high: '高',
          urgent: '緊急',
        },
        errors: {
          noCustomerTitle: '尚未選擇客戶',
          noCustomerBody: '請先選擇一個客戶',
          noSubjectTitle: '需要主題',
          noSubjectBody: '請填寫這次詢問的主題',
          noDescriptionTitle: '需要內容',
          noDescriptionBody: '請填寫詳細內容',
          createFailedTitle: '建立失敗',
        },
      },
      product: {
        titleCreate: '新增商品',
        titleEdit: '編輯商品',
        typeProduct: '商品',
        typeService: '服務',
        fieldName: '名稱',
        nameProductPlaceholder: '例如：手工皂',
        nameServicePlaceholder: '例如：60 分鐘按摩',
        fieldPrice: '價錢（{currency}）',
        pricePlaceholder: '0',
        fieldDuration: '時長（分鐘）',
        durationPlaceholder: '例如：60',
        fieldDescription: '描述',
        descriptionPlaceholder: '客戶會在對話中看到的介紹',
        fieldCategory: '分類',
        noCategory: '無分類',
        availableOn: '已上架',
        availableOnSub: '客戶可以在對話中看到',
        availableOff: '已下架',
        availableOffSub: '暫時不會顯示給客戶',
        advanced: '進階設定',
        fieldOrderingMode: '下單方式',
        orderingDirect: '直接下單',
        orderingInquiry: '詢問報價',
        fieldPriceRange: '價格範圍（選填）',
        priceMin: '最低',
        priceMax: '最高',
        fieldQuotationNotes: '報價備註',
        quotationPlaceholder: '例如：視款式與面積而定',
        fieldPaymentTiming: '付款時間',
        paymentFullUpfront: '全額預付',
        paymentDeposit: '訂金',
        paymentOnCompletion: '完成後付款',
        fieldDepositPercent: '訂金百分比 (%)',
        depositPlaceholder: '例如：30',
        fieldFulfillment: '交付方式',
        fulfillmentDelivery: '送貨',
        fulfillmentPickup: '自取',
        fulfillmentBoth: '送貨或自取',
        fulfillmentNone: '不適用',
        fieldServiceDelivery: '服務地點',
        serviceInStore: '到店',
        serviceAtCustomer: '到府',
        serviceOnline: '線上',
        serviceHybrid: '彈性',
        fieldServiceLocation: '地址 / 地點名',
        serviceLocationPlaceholder: '例如：台北市大安區 XX 路 3 樓',
        requiresBooking: '需要預約',
        requiresBookingHint: '開啟後，客戶在網店或與 AI 下單時都需要先選擇時段',
        saveEdit: '儲存變更',
        saveCreate: '建立商品',
        createHint: '建立後可以在詳情頁新增圖片',
        editOnWeb: '在桌面版開啟以編輯進階設定',
        errors: {
          nameTitle: '需要名稱',
          nameBody: '商品名稱是必填',
          priceTitle: '價錢錯誤',
          priceBody: '請輸入一個有效的價錢',
          durationTitle: '時長錯誤',
          durationBody: '請輸入一個有效的分鐘數',
          saveFailedTitle: '儲存失敗',
          unknownError: '未知錯誤',
        },
      },
    },
    businessLists: {
      orders: {
        todayTotal: '今日 {amount}',
        filterAll: '全部',
        filterPending: '待處理',
        filterConfirmed: '已確認',
        filterPaid: '已付款',
        filterCompleted: '完成',
        filterCancelled: '取消',
        unpaid: '未付款',
        unknownCustomer: '未知客戶',
        itemSummaryNone: '無項目',
        itemSummaryMore: '{first} 等 {total} 項 · 共 {totalQty} 件',
        empty: {
          allTitle: '尚無訂單',
          allSub: '客戶在 WhatsApp 下單後會出現在這裡',
          pendingTitle: '沒有待處理訂單',
          pendingSub: '好好休息一下',
          confirmedTitle: '沒有已確認訂單',
          paidTitle: '沒有已付款訂單',
          completedTitle: '沒有完成訂單',
          cancelledTitle: '沒有取消訂單',
        },
      },
      bookings: {
        allBranches: '所有分店',
        filterToday: '今日',
        filterUpcoming: '未到',
        filterPending: '待確認',
        filterConfirmed: '已確認',
        filterCompleted: '完成',
        filterCancelled: '取消',
        statusPending: '待確認',
        statusConfirmed: '已確認',
        statusCompleted: '完成',
        statusCancelled: '取消',
        statusNoShow: '缺席',
        fallbackCustomer: '未知客戶',
        fallbackService: '服務',
        cancelReasonPrefix: '取消原因：',
        dayCountLabel: '{count} 筆預約',
        btnConfirm: '確認',
        btnComplete: '完成',
        btnCancel: '取消',
        btnCheckIn: '標記已到',
        btnStartService: '開始',
        btnEndService: '完成服務',
        attendanceArrived: '已到',
        attendanceInService: '服務中',
        attendanceLate: '遲到',
        attendanceDone: '服務完',
        depositDue: '訂金未收',
        actionFailed: '操作失敗',
        confirmFailed: '確認失敗',
        completeFailed: '更新失敗',
        cancelConfirmTitle: '取消預約？',
        cancelConfirmBody: '確定要取消這筆預約嗎？客戶會自動收到通知。',
        cancelConfirmAction: '取消預約',
        cancelConfirmKeep: '不取消',
        cancelFailed: '取消失敗',
        unknownError: '未知錯誤',
        create: '建立預約',
        createFirst: '建立第一筆',
        empty: {
          upcomingTitle: '未來沒有預約',
          upcomingSub: '客戶透過網店、AI 預約或由你新增的預約都會顯示在此',
          pendingTitle: '沒有待確認預約',
          confirmedTitle: '沒有已確認預約',
          completedTitle: '沒有完成預約',
          cancelledTitle: '沒有取消預約',
          noShowTitle: '沒有缺席記錄',
        },
      },
      inquiries: {
        filterActive: '處理中',
        filterOpen: '新增',
        filterInProgress: '跟進中',
        filterResolved: '已解決',
        typeInquiry: '詢問',
        typeComplaint: '投訴',
        typeFeedback: '意見',
        typeSupport: '支援',
        priorityHigh: '高',
        priorityUrgent: '緊急',
        resolveConfirmTitle: '標記為已解決？',
        resolveConfirmBody: '確定這筆詢問已經處理完畢？',
        resolveConfirmAction: '已解決',
        resolveConfirmCancel: '還沒',
        updateFailed: '更新失敗',
        unknownError: '未知錯誤',
        unknownCustomer: '未知客戶',
        resolutionRecord: '處理記錄',
        viewConversation: '查看對話',
        btnMarkInProgress: '跟進中',
        btnMarkResolved: '已解決',
        createBtn: '建立詢問',
        trackCase: '工單',
        trackDeal: '商機',
        filterOverdue: '逾期',
        filterMine: '我的',
        unassigned: '未指派',
        ownerLabel: '負責人：{name}',
        assignToMe: '指派給我',
        dueIn: '{time}內要回',
        overdueBy: '逾期 {time}',
        stageLead: '新查詢',
        stageProposal: '提案中',
        stageNegotiation: '議價中',
        stageWon: '成交',
        stageLost: '流失',
        nextStep: '下一步：{text}',
        noValue: '未填金額',
        assignFailed: '指派失敗',
        empty: {
          activeTitle: '沒有待處理詢問',
          activeSub: '客戶的支援與投訴會出現在這裡，你也可以自己新增',
          openTitle: '沒有新詢問',
          inProgressTitle: '沒有跟進中的詢問',
          resolvedTitle: '沒有已解決記錄',
          closedTitle: '沒有已關閉記錄',
        },
      },
      products: {
        availableOfTotal: '{available} / {total} 上架中',
        searchPlaceholder: '搜尋商品名稱或分類',
        filterAll: '全部',
        filterProduct: '商品',
        filterService: '服務',
        tagService: '服務',
        tagPackage: '套票 · {count} 次',
        tagInquiryQuote: '詢問報價',
        tagMultiOption: '多選項',
        tagUnlisted: '下架',
        stateDraft: '草稿',
        stateArchived: '已封存',
        channelAiOnly: '只限 AI',
        channelStoreOnly: '只限網店',
        channelNone: '無通路',
        filterRestock: '要補貨',
        waitingCount: '{count} 人等候中',
        stockOut: '缺貨',
        stockLow: '剩 {count} 件',
        stockOk: '庫存 {count}',
        createBtn: '新增商品',
        addFirst: '新增第一件',
        empty: {
          noResultsTitle: '沒有搜尋結果',
          noResultsSub: '換個關鍵字試試',
          noProductsTitle: '尚無商品',
          noServicesTitle: '尚無服務',
          productCatalogSub: '新增商品或服務到 catalog，客戶在對話中就會看到',
          restockTitle: '沒有需要補貨的商品',
          restockSub: '所有已設低庫存線的貨品庫存均充足。',
        },
      },
    },
    customers: {
      title: '客戶',
      countLabel: '{count} 位',
      searchPlaceholder: '搜尋姓名、電話、email',
      orderCountOne: '{count} 筆',
      orderCountOther: '{count} 筆',
      emptyNoResults: '找不到搜尋結果',
      emptyNoSearchHint: '換個關鍵字試試',
      emptyNoCustomers: '尚無客戶',
      emptyNoCustomersHint: '客戶和你對話後會自動出現',
      lifecycle: {
        new: '新客',
        active: '活躍',
        atRisk: '流失風險',
        dormant: '沉睡',
        churned: '流失',
      },
    },
    conversations: {
      title: '對話',
      pendingCountLabel: '{count} 筆待接手',
      filterInbox: '收件匣',
      filterPending: '待接手',
      filterMine: '我處理',
      filterAll: '全部',
      youPrefix: '你：',
      aiPrefix: 'AI：',
      noMessage: '無訊息',
      unknownCustomer: '未知客戶',
      humanActiveBadge: '人工處理中',
      draftBadge: 'AI 草稿待批',
      capturedKnowledgeHeader: '知識草稿',
      empty: {
        inboxTitle: '收件匣已清空',
        inboxSub: '所有客戶對話都由 AI 處理中',
        pendingTitle: '沒有對話等待接手',
        pendingSub: '好好休息一下',
        mineTitle: '你尚未接手任何對話',
        mineSub: '到「待接手」分頁看看有沒有新客戶需要你',
        allTitle: '尚無任何對話',
        allSub: '客戶訊息會在這裡出現',
      },
    },
    login: {
      heroTagline: 'WhatsApp AI 助理 · 為你的生意而設',
      feature1: 'AI 自動回覆，支援多種語言',
      feature2: '訂單、預約、客戶管理一站式',
      feature3: '客戶聯繫時即時通知',
      title: '登入',
      subtitle: '繼續管理你的商家後台',
      googleSignIn: '使用 Google 登入',
      orEmail: '或使用 email',
      emailPlaceholder: 'your@email.com',
      sendLink: '寄送登入連結',
      termsPrefix: '登入即表示你同意我們的',
      termsOfService: '《服務條款》',
      termsAnd: '和',
      privacyPolicy: '《隱私政策》',
      checkEmailTitle: '請檢查你的信箱',
      checkEmailSentTo: '我們已經把登入連結寄到 {email}',
      checkEmailHint: '點入連結就會自動返回 app。',
      tryDifferentEmail: '使用其他 email',
      loginFailed: '登入失敗',
      googleLaunchFailed: '無法啟動 Google 登入',
      googleNoAuth: '未收到 Google 授權',
    },
    updateRequired: {
      title: '需要更新',
      body: '此版本的應用程式已不再支援。請更新後繼續管理您的店舖，您的資料不受影響。',
      cta: '立即更新',
    },
    needsOnboarding: {
      title: '請先在電腦完成設定',
      body: '你的帳戶已準備就緒，但需要先在 NovixOne 網頁後台完成商家設定，才能使用手機 App。約一分鐘即可完成。',
      ctaOpenWeb: '打開控制台',
      signOut: '登出',
    },
    loyaltySection: {
      heading: '會員',
      loadError: '無法載入會員資料',
      notMemberTitle: '未成為會員',
      notMemberHint: '客戶於第一次賺分或兌換時會自動登記。',
      currentPoints: '目前積分',
      lifetimePoints: '累積積分',
      expiringWarning: '有 {count} 分將到期',
      expiringEarliest: '最早：',
      unnamedProgram: '未命名計劃',
      typeStamp: '儲印花',
      stampLabel: '印花',
      currentLabel: '目前',
      lifetimeLabel: '累積',
      referralCode: '推薦碼',
      pendingHeading: '待核實兌換 ({count})',
      pendingPointsAndExpiry: '{points} 分 · 到期',
      ledgerHeading: '近期積分紀錄',
      balanceAfter: '餘 {value}',
      topTierEnteredAt: '最高等級 · 進入時間 {date}',
      reasonLabels: {
        order_paid: '訂單付款',
        order_completed: '訂單完成',
        appointment_completed: '預約完成',
        signup_bonus: '註冊獎賞',
        birthday_bonus: '生日禮',
        referral_earn: '推薦獎賞',
        tier_upgrade_bonus: '升級獎賞',
        stamp_earned: '儲印花',
        stamp_complete_bonus: '儲滿印花',
        manual_adjustment: '手動調整',
        bonus: '額外獎賞',
        redemption_hold: '兌換預留',
        redemption_commit: '兌換完成',
        redemption_cancel: '兌換取消',
        redemption: '兌換',
        expiry: '積分過期',
      },
      statusLabels: {
        pending: '待核實',
        approved: '已批',
        used: '已使用',
        cancelled: '已取消',
        expired: '已過期',
      },
    },
    support: {
      analytics: {
        title: '趨勢分析',
        periodWeek: '過去 7 天',
        periodMonth: '過去 30 天',
        empty: '尚無數據',
        labelRevenue: '營收',
        labelOrders: '訂單',
        labelConversations: '對話',
        labelAiContainment: 'AI 自主處理',
        chartDailyRevenue: '每日營收',
        chartDailyConversations: '每日對話',
        chartMin: '最低 {value}',
        chartMax: '最高 {value}',
        vsPrev: '相較上期',
        flat: '與上期相同',
      },
      knowledgeList: {
        title: '知識庫',
        loadError: '暫時無法載入知識庫',
        searchPlaceholder: '搜尋標題、內容或關鍵字',
        training: '部分知識仍在處理關鍵字…',
        emptySearchTitle: '找不到相關知識',
        emptyTitle: '知識庫還是空的',
        emptySearchHint: '試試用其他關鍵字搜尋。',
        emptyHint: '加些常見問題和政策，讓 AI 自動回答客戶。',
        addFirst: '新增第一條知識',
        categoryLabels: {
          faq: '常見問題',
          policy: '政策',
          info: '資訊',
          general: '一般',
        },
        deactivated: '已停用',
        processing: '處理中',
      },
      knowledgeGaps: {
        title: '知識缺口',
        hint: '客人問過但 AI 未能回答的問題，意思相近的已歸為一組。',
        emptyTitle: '暫時沒有未解決的缺口',
        emptyHint: 'AI 能回答客人所有問題 — 或尚未開始接待客人。',
        loadError: '載入缺口失敗',
        occurrences: (n: number) => `${n} 次`,
        customers: (n: number) => `${n} 位客人`,
        reopened: '已回答但仍找不到',
        reopenedHint: '已有答案，但客人再問時 AI 仍然找不到 — 請在該條知識補充預期問法，不要另開一條。',
        samples: '客人原話',
        createEntry: '建立知識',
        openEntry: '開啟知識',
        dismiss: '忽略',
        filterAll: '全部',
        filterMiss: '完全沒有覆蓋',
        filterLowConfidence: '低信心',
        filterEscalation: 'AI 放棄',
        filterReopened: '已回答但仍找不到',
      },
      knowledgeDrafts: {
        title: '待確認知識',
        hint: 'AI 從對話中擷取，經你確認後才會生效。',
        emptyTitle: '沒有待確認的項目',
        emptyHint: 'AI 擷取到新知識時會在此顯示。',
        loadError: '載入待審知識失敗',
        confidence: (pct: number) => `信心 ${pct}%`,
        approve: '確認',
        reject: '拒絕',
        approved: '已確認',
        rejected: '已拒絕',
        actionError: '操作失敗',
      },
      knowledgeEditor: {
        titleNew: '新增知識',
        titleEdit: '編輯知識',
        save: '儲存',
        saveError: '儲存失敗',
        unknownError: '未知錯誤',
        deleteConfirmTitle: '刪除知識？',
        deleteConfirmBody: '刪除後 AI 不會再用這條知識回答客戶。',
        deleteConfirmCancel: '取消',
        deleteConfirmAction: '刪除',
        deleteFailed: '刪除失敗',
        introHeading: '教 AI 如何回答客戶',
        introBody: '標題用客戶會問的方式寫，內容是你想 AI 怎麼回答。儲存後 AI 會自動學著用來回答相關問題。',
        titleLabel: '標題',
        titlePlaceholder: '例如：你們幾點開門？',
        titleHint: '用客戶會問的方式寫 — AI 會自動匹配類似的問法。',
        contentLabel: '內容',
        contentPlaceholder: '例如：我們早上十點到晚上七點，週日休息。連假會在 IG 公告。',
        categoryLabel: '分類',
        categoryLabels: {
          faq: '常見問題',
          policy: '政策',
          info: '資訊',
          general: '一般',
        },
        activeLabel: '啟用中',
        activeHint: '停用後 AI 不會再使用這條知識。',
        trainingNote: 'AI 還在學這條知識的關鍵字…',
        delete: '刪除知識',
      },
      soulEditor: {
        title: '語氣與守則',
        hint: '本頁設定套用於所有 AI 客服。系統基本規則（語言、不可捏造、確認後才下單）已自動生效。',
        save: '儲存',
        saveError: '儲存失敗',
        unknownError: '未知錯誤',
        unsavedTitle: '未儲存的修改',
        unsavedBody: '離開將會遺失未儲存的守則修改。',
        unsavedStay: '留在此頁',
        unsavedDiscard: '放棄修改',
        toneLabel: '語氣風格',
        tones: {
          friendly: '友善輕鬆',
          professional: '專業正式',
          casual: '隨意親切',
          enthusiastic: '熱情主動',
        },
        add: '加入',
        guidelinesLabel: '守則',
        guidelinesPlaceholder: '例：節日要主動提及優惠',
        guidelinesEmpty: '尚未設定 — 系統預設的基本規則會自動生效',
        guidelinesHint: '只需寫下你店舖獨有的規矩。',
        redLinesLabel: '紅線（絕對不可）',
        redLinesPlaceholder: '例：不要回答其他店家的產品',
        redLinesEmpty: '尚未設定紅線',
        redLinesHint: '寫下 AI 絕對不可做的事。',
      },
      agentsList: {
        title: 'AI 客服',
        introHeading: 'AI 代理管理',
        introBody: '每個代理有自己的人格與技能。大部分商家只需要一個 — 進去修改人格描述並新增技能即可。',
        emptyTitle: '尚未建立任何代理',
        emptyBody: '通常系統會在 onboarding 自動幫你建立一個。如果沒有，請聯繫支援。',
        active: '使用中',
        descriptionMissing: '尚未設描述',
        defaultBadge: '預設',
        inactive: '已停用',
        channelsNone: '未綁定渠道（預設路由）',
        soulLink: '語氣與守則',
        soulLinkHint: '語氣、守則、紅線 — 所有 AI 客服共用',
      },
      agentsEditor: {
        titleFallback: '代理',
        save: '儲存',
        saveError: '儲存失敗',
        unknownError: '未知錯誤',
        notFound: '找不到代理',
        deactivateTitle: '停用代理？',
        deactivateBody: '停用後這個代理不會再處理任何對話。',
        deactivateCancel: '取消',
        deactivateAction: '停用',
        activeLabel: '使用中',
        inactiveLabel: '已停用',
        activeHint: '這個代理正在處理客戶對話',
        inactiveHint: '停用的代理不會處理任何對話',
        nameLabel: '名稱',
        namePlaceholder: '例如：主要助理',
        descLabel: '描述',
        descPlaceholder: '簡短描述這個代理的角色',
        personaLabel: '人格提示詞',
        personaPlaceholder: '用自然語言描述這個 AI 的個性、口吻與行為模式。這裡的指令會加到系統 prompt 裡。',
        personaHint: '與 AI 個性（Soul）分開 — Soul 是全店共用的語氣與紅線，這裡是這個代理獨有的指令。',
        skillsLabel: '技能',
        skillsAdd: '新增',
        skillsEmptyTitle: '尚無技能',
        skillsEmptyBody: '技能是特定場景的指令 — 例如「處理投訴」、「預約流程」。AI 會在偵測到相關對話時自動啟用。',
        skillLearningLabel: '學習觸發詞中',
        skillDeactivated: '已停用',
        sheetCreateTitle: '新增技能',
        sheetEditTitle: '編輯技能',
        skillNameLabel: '名稱',
        skillNamePlaceholder: '例如：處理投訴',
        skillInstructionsLabel: '指令',
        skillInstructionsPlaceholder: '用自然語言描述 AI 在這個場景應該怎麼做。例如：「客戶投訴時，先道歉，然後詢問具體狀況…」',
        skillInstructionsHint: '觸發詞會自動由 AI 生成 — 你不用手動設定。',
        skillSaveCreate: '建立技能',
        skillSaveEdit: '儲存變更',
        skillDelete: '刪除技能',
        skillDeleteConfirmTitle: '刪除技能？',
        skillDeleteConfirmBody: '刪除「{name}」之後 AI 不會再使用這個技能。',
        skillDeleteConfirmCancel: '取消',
        skillDeleteConfirmAction: '刪除',
        skillDeleteFailed: '刪除失敗',
        defaultBadge: '預設',
        defaultHint: '預設 AI 客服：未綁定渠道的對話均由其回覆，不可停用。',
        setDefault: '設為預設',
        cannotDeactivateDefault: '預設 AI 客服不可停用，請先將另一個設為預設。',
        inactiveCannotBeDefault: '已停用的 AI 客服不可設為預設，請先啟用。',
        skillGlobal: '全域',
        skillTriggerFailed: '觸發詞生成失敗',
        skillTriggersLabel: '觸發關鍵字',
        skillTriggersPlaceholder: '用逗號分隔，例：退款， complaint',
        skillTriggersHint: '留空由 AI 自動生成。英文需為完整單字才算符合；中文只要包含即算符合。',
        skillActiveLabel: '啟用技能',
        skillRegenerate: '重新生成觸發詞',
        skillRegenerateTitle: '重新生成觸發詞？',
        skillRegenerateBody: '將消耗 AI credits 根據指令重新生成，舊觸發詞會保留至新觸發詞生成完成。',
        skillNameTaken: '已有同名技能，請改個名。',
        skillDeleteGlobalConfirmBody: '「{name}」是全域技能，所有 AI 客服都會使用。刪除後無法復原。',
        skillSaveFailed: '儲存失敗',
      },
      staffList: {
        title: '服務人員',
        introHeading: '服務人員管理',
        introBody: '加入你的團隊成員，指定他們可提供哪些服務。預約時會自動分派（不論透過網店、AI 或人手新增）。',
        emptyTitle: '尚未加入任何服務人員',
        emptyBody: '加入人員後，新預約可以自動分派，或由客戶在網店或對話中自行選擇。',
        allServices: '所有服務',
        someServices: '{count} 項服務',
        deactivated: '已停用',
      },
      staffEditor: {
        titleNew: '新增人員',
        titleEdit: '編輯人員',
        save: '儲存',
        saveError: '儲存失敗',
        unknownError: '未知錯誤',
        deleteConfirmTitle: '刪除人員？',
        deleteConfirmBody: '刪除「{name}」— 若有未完成的預約會改為停用。',
        deleteConfirmCancel: '取消',
        deleteConfirmAction: '刪除',
        deleteFailed: '刪除失敗',
        nameLabel: '名稱',
        namePlaceholder: '例如：Amy / 小美',
        phoneLabel: '電話',
        phonePlaceholder: '員工聯絡電話',
        emailLabel: 'Email',
        colorLabel: '代表色',
        activeLabel: '使用中',
        inactiveLabel: '已停用',
        activeHint: '停用後不會再被分派預約',
        servicesLabel: '可提供的服務',
        servicesAll: '所有服務',
        servicesSome: '指定服務',
        pickCategoriesHint: '選分類 = 自動包含分類下所有服務',
        pickServicesHint: '或者逐項選擇服務',
        noServicesHint: '尚無可指定的服務 — 先到商品新增服務類型的項目',
        delete: '刪除人員',
      },
      assistant: {
        title: 'AI 助理',
        subtitle: '幫你管理商品、訂單、預約、客戶',
        voiceLocalePickerTitle: '語音辨識語言',
        voiceLocaleAuto: '自動（跟隨手機）',
        voiceLocaleCancel: '取消',
        voiceRecognitionFailed: '語音辨識失敗',
        voiceUnsupportedTitle: '不支援語音',
        voiceUnsupportedBody: '這部裝置或這個 build 不支援語音辨識。',
        voiceStartFailed: '語音啟動失敗',
        voiceListeningChip: '聆聽中…請說',
        voiceListeningPlaceholder: '聆聽中…',
        noPermissionTitle: '沒有權限',
        noCameraPermission: '需要相機權限才能使用',
        noPhotoPermission: '需要相片權限才能使用',
        iCloudTitle: '相片還在 iCloud',
        iCloudBody: '這張相片還沒下載到手機，請開啟 iCloud 等下載完再選擇。',
        imageUploadFailed: '圖片上傳失敗',
        attachSheetTitle: '加圖片',
        attachCamera: '拍照',
        attachLibrary: '從相簿選',
        attachCancel: '取消',
        thinking: 'AI 助理思考中…',
        sendFailed: '傳送失敗',
        uploadingImage: '上傳圖片中…',
        clearTitle: '清空對話？',
        clearBody: '清空後 AI 不會記得之前的對話。',
        clearCancel: '取消',
        clearAction: '清空',
        clearFailed: '清空失敗',
        inputPlaceholder: '請 AI 助理幫你…',
        imageFallbackPrompt: '請看這張圖片',
        emptyTitle: 'AI 助理',
        emptyBody: '你的 24/7 AI 員工\n有什麼想做或想知道，直接問我',
        emptySuggestions: {
          followUp: '今天有沒有客戶需要跟進？',
          addProduct: '拍張相片新增產品（按下面 📷）',
          tomorrowBookings: '明天有多少預約？',
          lapsedCustomers: '哪些客戶一個月沒回來？',
        },
        imageCountBadge: '{count} 張圖片',
        localeLabels: {
          zhHK: '廣東話',
          zhTW: '繁體中文（台灣）',
          zhCN: '簡體中文',
          enUS: 'English (US)',
          enGB: 'English (UK)',
          enAU: 'English (Australia)',
          jaJP: '日本語',
          koKR: '한국어',
          thTH: 'ภาษาไทย',
          viVN: 'Tiếng Việt',
        },
      },
    },
    components: {
      phoneInput: {
        placeholder: '電話號碼',
      },
      countryPicker: {
        title: '選擇國家 / 地區',
        done: '完成',
        searchPlaceholder: '搜尋國家 / 地區碼',
        emptyResults: '沒有搜尋結果',
      },
      topupSheet: {
        openPaymentFailed: '無法開啟付款',
        unknownError: '未知錯誤',
        title: '加購回覆',
        subtitle: '一次付款，回覆跨月都能使用',
        recommended: '推薦',
        replyCountLabel: '{count} 次 AI 回覆',
        perReply: '{price}/次',
        footer: '使用 Stripe 付款，付完會自動加入你的帳戶',
        bundleSmall: '小量',
        bundleMedium: '中量',
        bundleLarge: '大量',
      },
      customerEditSheet: {
        title: '編輯客戶',
        saveError: '儲存失敗',
        unknownError: '未知錯誤',
        fieldName: '名稱',
        fieldNamePlaceholder: '客戶名',
        fieldPhone: '電話',
        fieldEmail: 'Email',
        fieldTags: '標籤',
        tagInputPlaceholder: '輸入標籤後按 Enter',
        save: '儲存變更',
      },
      customerPicker: {
        titleDefault: '選擇客戶',
        addNew: '新客戶',
        searchPlaceholder: '搜尋姓名或電話',
        emptyNoResults: '沒有搜尋結果',
        emptyNoCustomers: '尚無客戶',
        addNewHint: '點右上「新客戶」新增',
        newCustomerTitle: '新客戶',
        save: '儲存',
        nameRequiredTitle: '需要名稱',
        nameRequiredBody: '客戶名稱為必填',
        phoneInvalidTitle: '電話格式錯誤',
        phoneInvalidBody: '請檢查國家碼與本地號碼的數字長度。',
        createFailedTitle: '建立失敗',
        nameLabel: '姓名',
        namePlaceholder: '客戶的稱呼',
        phoneLabel: '電話',
        phoneHint: '有電話的話，客戶之後用 WhatsApp 聯絡你會自動對應',
      },
      notesSection: {
        title: '備註',
        composerPlaceholder: '寫下關於這位客戶的備註…',
        visibleToAi: 'AI 可見',
        hiddenFromAi: 'AI 不可見',
        addBtn: '新增',
        loadError: '載入備註失敗',
        emptyLabel: '尚無備註',
        deleteConfirmTitle: '刪除備註？',
        deleteConfirmBody: '刪除後無法復原。',
        deleteConfirmCancel: '取消',
        deleteConfirmAction: '刪除',
        createFailedTitle: '新增失敗',
        deleteFailedTitle: '刪除失敗',
        unknownError: '未知錯誤',
      },
      memoriesSection: {
        title: 'AI 記憶 · {count}',
        titleNoCount: 'AI 記憶',
        addBtn: '新增',
        cancelBtn: '取消',
        keyPlaceholder: '標識，例如：付款方式',
        valuePlaceholder: '內容，例如：LINE Pay',
        saveBtn: '儲存記憶',
        loadError: '載入記憶失敗',
        emptyLabel: '尚無 AI 記憶 — AI 對話後會自動提取',
        deleteConfirmTitle: '刪除記憶？',
        deleteConfirmBody: '「{key}」的記憶會被移除。',
        deleteConfirmCancel: '取消',
        deleteConfirmAction: '刪除',
        createFailedTitle: '新增失敗',
        deleteFailedTitle: '刪除失敗',
        unknownError: '未知錯誤',
        categoryLabels: {
          preference: '偏好',
          behavior: '行為',
          personal: '個人',
          complaint: '投訴',
        },
        sourceManual: '手動',
        sourceAi: 'AI 提取',
        confidenceLabel: '信心 {percent}%',
        mentionCount: '提及 {count} 次',
      },
    },
    detail: {
      customer: {
        titleFallback: '客戶',
        loadError: '客戶載入失敗',
        back: '返回',
        statTotalSpend: '總消費',
        statTotalOrders: '總訂單',
        statTotalOrdersSuffix: '筆',
        statEngagement: '互動分數',
        statHealth: '健康分數',
        sectionContact: '聯絡資訊',
        sectionTags: '標籤',
        sectionActivity: '活動',
        sectionTimeline: '時間線 · {count}',
        contactPhone: '電話',
        contactEmail: 'Email',
        notProvided: '未提供',
        lastSeen: '上次聯絡',
        lastOrder: '上次下單',
        summaryFallback: '（暫無摘要）',
        messageCount: '{count} 則訊息',
        orderStatus: {
          pending: '待處理',
          confirmed: '已確認',
          paid: '已付款',
          completed: '完成',
          cancelled: '取消',
        },
        conversationStatus: {
          aiActive: 'AI 處理中',
          pendingHandoff: '待接手',
          humanActive: '人工中',
          resolved: '已完成',
        },
        unpaid: '未付款',
        lifecycle: {
          new: '新客',
          active: '活躍',
          atRisk: '流失風險',
          dormant: '沉睡',
          churned: '流失',
        },
        inquiryTypes: {
          inquiry: '詢問',
          complaint: '投訴',
          feedback: '回饋',
          support: '支援',
        },
        inquiryStatus: {
          open: '待處理',
          inProgress: '處理中',
          resolved: '已解決',
          closed: '已關閉',
        },
        inquiryPriority: {
          low: '低',
          normal: '普通',
          high: '高',
          urgent: '緊急',
        },
      },
      conversation: {
        approval: {
          title: '待批准',
          subtitle: 'AI 想在你的系統執行以下操作，需要同事批准：',
          approve: '批准',
          reject: '拒絕',
          approveConfirmTitle: '批准此操作？',
          approveConfirmBody: '將立即在你的系統執行，並通知客人結果。',
          rejectConfirmTitle: '拒絕此操作？',
          rejectConfirmBody: '不會執行；客人會收到「同事未能批准」。',
          noPermission: '你沒有批准此類操作的權限',
          expires: '{time} 前有效',
          done: '已執行 ✅',
          failed: '執行失敗 — 已轉交同事跟進',
          rejected: '已拒絕',
          alreadyDecided: '已有同事處理',
          expired: '已過期',
        },
        externalTools: {
          ranInYourSystem: 'AI 在你的系統執行',
          args: '參數',
          result: '結果',
          noFacts: '除狀態外沒有其他回傳資料。',
          close: '關閉',
          states: {
            read: '已查詢',
            done: '已完成',
            awaiting_customer: '待客人確認',
            awaiting_approval: '待同事批准',
            unavailable: '無法連接',
            blocked: '未執行',
          },
        },
        customerFallback: '客戶',
        loadError: '對話載入失敗',
        back: '返回',
        unknownError: '未知錯誤',
        takeoverClaimedTitle: '已被接手',
        takeoverClaimedBody: '另一位同事已經接手這個對話。',
        takeoverClaimedOk: '了解',
        takeoverFailed: '接手失敗',
        returnToAiFailed: '操作失敗',
        sendFailed: '發送失敗',
        draftFailed: 'AI 起草失敗',
        draftBtnIdle: 'AI 起草回覆',
        pendingDraftTitle: 'AI 已擬好回覆 · 等你審批',
        pendingDraftSend: '發送',
        pendingDraftDiscard: '捨棄',
        pendingDraftHint: '如有需要可在此修改。發送後不會接管對話，AI 會繼續擬稿。',
        draftBtnLoading: 'AI 思考中…',
        inputPlaceholder: '輸入回覆...',
        returnToAi: '交回 AI 跟進',
        takeoverBtn: '接手對話',
        claimBtn: '主動接手',
        conversationResolved: '對話已完成',
        emptyMessages: '尚無訊息',
        episodeStarted: '新一段對話',
        episodePrevious: '上一段：{reason} · {handled}',
        episodeCloseReasons: { settled: '已完成（自動）', staff: '同事已結束', ai: 'AI 已結束', api: '系統已結束', idle: '閒置結束', merged: '舊對話' },
        episodeHandledBy: { ai_only: 'AI 處理', handed_off: '已轉交同事', human: '同事處理' },
        aiAssistantLabel: 'AI 助理',
        fromWhatsAppApp: '經 WhatsApp Business App',
        importedFromWhatsAppApp: '由 WhatsApp Business App 匯入',
        rateUp: '此回覆良好',
        rateDown: '此回覆有問題',
        rateWithdraw: '取消評分',
        rateReasonTitle: '有什麼問題？',
        rateReasons: {
          wrong_info: '資料錯誤',
          wrong_tone: '語氣不當',
          should_escalate: '應該轉真人',
          hallucination: '捏造 / 胡亂回答',
          other: '其他',
        },
        rateFailed: '評分失敗',
        mediaAudio: '語音訊息',
        mediaDocument: '文件附件',
        mediaTapOpen: '點擊開啟',
        insightsTitle: 'AI 洞察',
        insightsUrgentBadge: '有急需跟進',
        insightsCountBadge: '{count} 項跟進',
        insightsUnhappyBadge: '客戶不滿',
        objectionsLabel: '客戶疑慮',
        actionsSuggestionLabel: '建議跟進',
        actionComplete: '標記完成',
        actionDismiss: '略過',
        purchaseIntent: '購買意向 {percent}%',
        urgencyHigh: '高緊急度',
        urgencyMedium: '中緊急度',
        sentiment: {
          veryNegative: '非常負面',
          negative: '負面',
          neutral: '中性',
          positive: '正面',
          veryPositive: '非常正面',
        },
        priority: {
          low: '低',
          medium: '中',
          high: '高',
          urgent: '急',
        },
        statusPill: {
          pendingHandoff: '待接手',
          humanActive: '人工',
          aiActive: 'AI',
          resolved: '完成',
        },
        statusLabel: {
          aiActive: 'AI 處理中',
          pendingHandoff: '等候負責人接手',
          humanActive: '人工處理中',
          resolved: '已完成',
        },
        attachImage: '附加圖片',
        imageReady: '圖片已準備好',
        imagePermDeniedTitle: '需要相片權限',
        imagePermDeniedBody: '請在手機設定中允許 NovixOne 存取相片',
        imageTooLargeTitle: '圖片太大',
        imageTooLargeBody: '請選擇 8 MB 以下的圖片',
        resolveBtn: '結束對話',
        resolveConfirmTitle: '結束對話？',
        resolveConfirmBody: '結束後對話會移至已解決列表，之後可重新開啟。',
        resolveConfirmOk: '結束',
        resolveFailed: '結束失敗',
        cancel: '取消',
        moreActions: '更多',
        actionTransfer: '🔁 轉交同事',
        actionSnooze: '⏰ 暫緩處理',
        actionUnsnooze: '🔔 取消暫緩',
        snoozeTitle: '暫緩此對話',
        snoozeBody: '暫緩期間會從預設列表中隱藏',
        snooze1h: '1 小時',
        snooze4h: '4 小時',
        snoozeTomorrow: '明天早上 9 點',
        snoozeFailed: '暫緩失敗',
        transferComingSoonTitle: '選擇同事功能即將推出',
        transferComingSoonBody: '請暫時在電腦版進行轉交',
      },
      product: {
        titleFallback: '商品',
        loadError: '商品載入失敗',
        back: '返回',
        unknownError: '未知錯誤',
        deleteConfirmTitle: '下架這件商品？',
        deleteConfirmBody: '下架後客戶不會再看到，你隨時可以在編輯中重新上架。',
        deleteConfirmCancel: '取消',
        deleteConfirmAction: '下架',
        deleteFailedTitle: '失敗',
        noPermissionTitle: '沒有權限',
        noCameraPermission: '需要相機權限才能使用',
        noPhotoPermission: '需要相片權限才能使用',
        iCloudTitle: '相片還在 iCloud',
        iCloudBody: '這張相片未下載到手機，請開啟 iCloud 等下載完再選擇。',
        uploadFailedTitle: '上傳失敗',
        imageActionSheet: {
          camera: '拍照',
          library: '相簿',
          cancel: '取消',
          addImageTitle: '新增相片',
        },
        deleteImageTitle: '刪除這張相片？',
        deleteImageBody: '刪除後無法復原。',
        genericFailed: '失敗',
        uploading: '上傳中…',
        addFirstImage: '新增第一張相片',
        tagService: '服務',
        tagPackage: '套票 · {count} 次',
        tagInquiryQuote: '詢問報價',
        tagMultiOption: '多選項',
        tagUnlisted: '下架',
        stateDraft: '草稿',
        stateArchived: '已封存',
        channelAiOnly: '只限 AI',
        channelStoreOnly: '只限網店',
        channelNone: '無通路',
        perfTitle: '過去 30 日',
        perfUnits: '賣出',
        perfRevenue: '營業額',
        perfWaiting: '等候到貨',
        perfNoSales: '過去 30 日未賣出過。',
        perfLastSold: '最後賣出：{date}',
        aiPreviewTitle: 'AI 看到的內容',
        aiPreviewShow: '查看 AI 收到的內容',
        aiPreviewHide: '收起',
        aiPreviewUnreachable: 'AI 目前無法存取此商品 —— 並非內容問題，而是狀態或通路設定。',
        aiPreviewReadiness: '就緒 {ready}/{of}',
        openOnDesktop: '在電腦繼續編輯',
        openOnDesktopHint: '完整編輯（規格、修飾項、排程價）請使用網頁版',
        durationMinutes: '{minutes} 分鐘',
        sectionDescription: '描述',
        sectionVariants: '選項',
        variantsEmpty: '尚無選項 — 例如不同 size、顏色、時長',
        addVariant: '新增選項',
        stockOut: '缺貨',
        stockLow: '剩 {count} 件',
        stockOk: '庫存 {count}',
        stockRemaining: '剩 {count}',
        stockTotal: '庫存 {count}',
        variantAvailable: '上架',
        variantUnavailable: '下架',
        variantSheet: {
          createTitle: '新增選項',
          editTitle: '編輯選項',
          nameLabel: '名稱',
          namePlaceholder: '例如：大碼 / 60 分鐘 / 紅色',
          priceLabel: '價錢（留空 = 跟隨主商品）',
          pricePlaceholder: '0',
          stockLabel: '庫存數量',
          stockPlaceholder: '留空 = 不追蹤庫存',
          available: '上架中',
          unavailable: '已下架',
          saveCreate: '建立選項',
          saveEdit: '儲存變更',
          delete: '刪除選項',
          deleteConfirmTitle: '刪除選項？',
          deleteConfirmBody: '刪除「{name}」後無法還原。',
          deleteConfirmCancel: '取消',
          deleteConfirmAction: '刪除',
          deleteFailed: '刪除失敗',
          createFailed: '建立失敗',
          saveFailed: '儲存失敗',
          unknownError: '未知錯誤',
        },
      },
      order: {
        titleFallback: '訂單',
        loadError: '訂單載入失敗',
        back: '返回',
        unknownError: '未知錯誤',
        unknownCustomer: '未知客戶',
        confirmFailed: '確認失敗',
        updateFailed: '更新失敗',
        cancelFailed: '取消失敗',
        markPaidTitle: '確認收款？',
        markPaidBody: '標記為「已付款」之後，客戶會收到確認通知。',
        markPaidCancel: '取消',
        markPaidConfirm: '已收款',
        cancelOrderTitle: '取消訂單？',
        cancelOrderBody: '取消後無法復原，客戶會自動收到通知。',
        cancelOrderCancel: '不取消',
        cancelOrderAction: '取消訂單',
        actionConfirm: '確認訂單',
        actionMarkPaid: '確認收款',
        actionShip: '標記已發貨',
        actionComplete: '完成',
        actionCancel: '取消訂單',
        sectionItems: '訂單項目',
        itemCount: '{count}',
        total: '總計',
        collectedLabel: '已收',
        outstandingLabel: '未收',
        overpaidLabel: '多收（待退）',
        revisionLabel: '已修訂 {count} 次',
        sectionOther: '其他資訊',
        paymentMethodLabel: '付款方式',
        notesLabel: '備註',
        status: {
          pending: '待處理',
          confirmed: '已確認',
          paid: '已付款',
          completed: '完成',
          cancelled: '取消',
        },
        unpaid: '未付款',
      },
    },
    home: {
      welcome: {
        greetingNight: '深夜',
        greetingMorning: '早安',
        greetingAfternoon: '午安',
        greetingEvening: '晚安',
        fallbackName: '老闆',
        tagline: '今天和 NovixOne 一起工作',
      },
      aiActions: {
        sectionLabel: 'AI 建議跟進',
        itemCountLabel: '{count} 項',
        empty: '目前沒有建議要跟進，AI 會在對話後自動挑出最值得你處理的客戶',
        types: {
          followUp: '跟進',
          winBack: '挽回',
          crossSell: '加購推薦',
          resolveIssue: '處理問題',
          nurture: '培養關係',
          thankVip: '感謝 VIP',
          checkSatisfaction: '確認滿意度',
        },
        priority: {
          low: '低',
          medium: '中',
          high: '高',
          urgent: '急',
        },
      },
      attention: {
        atRisk: '流失風險',
        highIntent: '高意向',
        openInquiries: '待處理詢問',
      },
      bookings: {
        sectionLabel: '今日預約',
        viewAll: '查看全部',
        statusPending: '待確認',
        statusConfirmed: '已確認',
        fallbackCustomer: '未知客戶',
        fallbackService: '服務',
      },
      stats: {
        sectionLabel: '今日概況',
        revenue: '今日營收',
        orders: '今日訂單',
        conversations: '今日對話',
        quotaUsed: '本月用量',
        trendsTitle: '趨勢分析',
        trendsSub: '過去 7 天 / 30 天的營收、對話和訂單',
        couponCheckTitle: '核對優惠券',
        couponCheckSub: '客戶在門市出示優惠碼時即時核對',
      },
      activeConvs: {
        countLabel: '{count} 筆進行中對話',
        viewAll: '查看全部對話 →',
      },
      followUps: {
        sectionLabel: '今日跟進',
        onRecord: '跟進',
        noneOnRecord: '此單沒有待處理的跟進。',
        viewAll: '查看全部',
        mine: '我的',
        overdue: '{count} 項逾期',
        empty: '今日沒有待跟進事項',
        noDate: '無日期',
        done: '完成',
        markDone: '標記完成',
        laterAction: '稍後跟進',
        created: '已排跟進',
        outcomePrompt: '結果如何？',
        outcomes: {
          done: '已完成',
          no_answer: '聯絡不上',
          rescheduled: '改期',
          not_needed: '無需處理',
        },
        quick: {
          hour: '1 小時後',
          tonight: '今晚',
          tomorrow: '明天',
          nextWeek: '下星期一',
        },
      },
      pending: {
        sectionLabel: '待接手對話',
        viewAll: '查看全部',
        noMessage: '無訊息',
        unknownCustomer: '未知客戶',
      },
      quota: {
        sectionLabel: 'AI 回覆用量',
        thisMonth: '本月回覆',
        topupRemaining: '另外有 {count} 次加購回覆',
        topup: '加購回覆',
        topupWarn: '快用完 — 加購回覆',
      },
      fab: {
        askAssistant: '詢問助理',
      },
      channelDown: {
        title: 'WhatsApp {number} 已中斷',
        body: 'AI 目前不會回覆這個號碼的客人。請在網頁版「設定 › 通訊渠道」重新連接。',
      },
      aiPause: {
        title: 'AI 已暫停',
        hint: '新訊息會直接等你接手',
        hintWithDuration: '已暫停 {duration} · 新訊息會直接等你接手',
        lessThanMinute: '少於 1 分鐘',
        resume: '恢復 AI',
      },
    },
    bookingAdvanced: {
      cancel: '取消',
      save: '儲存',
      keep: '保留',
      unknownError: '發生問題，請再試一次。',
      failedTitle: '操作未完成',
      reasonLabel: '原因（必填）',
      reasonPlaceholder: '例如：補償一堂、預約出錯',
      waitlist: {
        title: '候補名單',
        openButton: '候補 · {count}',
        filterOpen: '等候中',
        filterActive: '未通知',
        filterNotified: '已通知',
        filterConverted: '已預約',
        filterExpired: '已過期',
        filterCancelled: '已關閉',
        filterAll: '全部',
        allServices: '全部服務',
        empty: '暫時沒有人候補',
        emptyHint: '客人找不到合適時段時可加入候補，有空位便可立即通知對方。',
        dateWindow: '{from} 至 {to}',
        anyTime: '任何時間',
        timeWindow: '{from}–{to} 開始',
        timeFromOnly: '{from} 之後開始',
        timeToOnly: '{to} 之前開始',
        everyDay: '任何日子',
        notifiedTimes: '已通知 {count} 次',
        anyStaff: '任何員工',
        status: {
          active: '等候中',
          notified: '已通知',
          converted: '已預約',
          expired: '已過期',
          cancelled: '已關閉',
        },
        add: '新增',
        addTitle: '加入候補名單',
        customer: '客人',
        customerPlaceholder: '選擇客人',
        service: '服務',
        servicePlaceholder: '選擇服務',
        staff: '員工',
        branch: '分店',
        dateFrom: '由',
        dateTo: '至',
        earliestStart: '最早開始',
        latestStart: '最遲開始',
        weekdays: '可接受星期',
        weekdayShort: ['日', '一', '二', '三', '四', '五', '六'],
        note: '備註',
        notePlaceholder: '例如：希望安排靠窗位置',
        submit: '加入候補',
        added: '已加入候補名單',
        alreadyOn: '此客人已在此候補名單中',
        prefsUpdated: '已更新此客人的候補時段',
        closeEntry: '關閉',
        closeConfirmTitle: '關閉此候補？',
        closeConfirmBody: '紀錄會保留，但之後有空位亦不會再配對此客人。',
        remove: '移除',
        removeConfirmTitle: '移除此候補？',
        removeConfirmBody: '此候補將被永久刪除。',
        notify: '通知',
        notifyTitle: '通知 {name} 有空位',
        notifyHint: '不會為客人保留位置，客人需自行預約。傳送前會再次檢查該時段是否仍有空位。',
        slotDate: '日期',
        slotTime: '開始時間',
        notifySend: '傳送',
        notified: '已通知',
        matchesTitle: '{count} 位客人正在等候此時段',
        errors: {
          SLOT_TAKEN: '此時段已沒有空位',
          SLOT_PAST: '此時段已過',
          OPTED_OUT: '此客人不接收推廣訊息',
          ALREADY_NOTIFIED: '已就此時段通知過此客人',
          ENTRY_CLOSED: '此候補已完結',
          NOT_A_MATCH: '此時段並非客人所需的服務或分店',
          NOT_SENT: '無法傳送（沒有可用渠道或範本未獲批）',
          SEND_FAILED: '傳送失敗，請再試',
          SLOT_NOT_FOUND: '找不到此時段的服務、分店或員工',
          APPOINTMENT_NOT_FOUND: '此預約已不存在',
          INVALID_RANGE: '日期範圍無效',
          INVALID_TIME: '時間無效',
          INVALID_WEEKDAYS: '請至少選擇一天',
          INVALID_DATE: '日期無效',
          NOT_A_SERVICE: '只有可預約的服務才可加入候補',
          LOCATION_NOT_FOUND: '找不到此分店',
          LOCATION_REQUIRED: '請選擇分店',
          MODULE_DISABLED: '進階預約功能未開啟',
          NOT_FOUND: '此候補已不存在',
          FORBIDDEN: '你沒有權限執行此操作',
        },
      },
      packages: {
        sectionTitle: '套票（{count}）',
        left: '剩 {left} 堂',
        usage: '已用 {used} · 已預約 {reserved} · 共 {total} 堂',
        expires: '有效期至 {date}',
        noExpiry: '無到期日',
        target: '適用：{service}',
        status: {
          active: '有效',
          completed: '已用完',
          cancelled: '已取消',
          expired: '已過期',
        },
        cancelReason: {
          staff: '員工取消',
          order_refunded: '訂單已退款',
          order_cancelled: '訂單已取消',
          payment_voided: '付款已作廢',
        },
        screenTitle: '套票',
        bookingsTitle: '使用此套票的預約',
        noBookings: '尚未有預約使用此套票',
        manage: '調整',
        sessions: '堂數',
        sessionsHelp: '+ 表示退回堂數予客人，− 表示扣減堂數',
        newExpiry: '新到期日：{date}',
        extendBy: '+{days} 日',
        keepExpiry: '不變',
        apply: '儲存調整',
        cancelPackage: '取消套票',
        cancelConfirmTitle: '取消此套票？',
        cancelConfirmBody: '取消後不可再用於預約。已有的 {reserved} 個未來預約會保留其堂數。',
        reactivate: '重新啟用',
        reviveConfirmTitle: '重新啟用已退款的套票？',
        reviveConfirmBody: '此套票因訂單退款、取消或付款作廢而被取消。重新啟用即表示再次發放這些堂數。',
        reviveConfirm: '仍然重新啟用',
        reasonRequired: '請先填寫原因',
        saved: '套票已更新',
        errors: {
          OVER_TOTAL: '剩餘堂數不足，部分堂數已被未來預約佔用',
          NEGATIVE: '已用堂數不可少於 0',
          BELOW_SPENT: '這些堂數已被已完成的預約使用，請先撤銷相關預約',
          EXPIRY_BEFORE_BOOKING: '有未來預約在新到期日之後使用此套票',
          EXPIRY_PASSED: '套票已過期，需先延長有效期才可重新啟用',
          REVIVE_NEEDS_CONFIRM: '此套票的訂單已退款或取消，需確認後才可重新啟用',
          NOT_FOUND: '此套票已不存在',
          REASON_REQUIRED: '請先填寫原因',
          NOTHING_TO_CHANGE: '沒有需要修改的內容',
          BAD_EXPIRY: '新到期日無效',
          FORBIDDEN: '只有具退款權限的同事才可調整套票',
        },
      },
      booking: {
        title: '預約',
        loadError: '無法載入此預約',
        back: '返回',
        customer: '客人',
        service: '服務',
        when: '時間',
        staff: '員工',
        branch: '分店',
        amount: '金額',
        packagePaid: '套票 · {n}/{m}',
        packagePaidShort: '套票',
        deposit: '訂金',
        depositDue: '未收訂金',
        depositPaid: '已收訂金',
        order: '訂單',
        seriesLabel: '循環預約 第 {i}/{t} 次 · {frequency}',
        seriesFrequency: {
          weekly: '每週',
          biweekly: '每兩週',
          triweekly: '每三週',
          monthly: '每月',
        },
        seriesStatus: {
          active: '進行中',
          completed: '已完成',
          cancelled: '已取消',
        },
        seriesSessions: '所有堂數',
        seriesThis: '本預約',
        cancelRemaining: '取消餘下堂數',
        cancelRemainingConfirm: '取消餘下 {count} 堂？已完成及已過去的堂數會保留，相關訂單一併取消。',
        cancelRemainingDone: '已取消 {count} 堂',
        ordersHeld: '以下訂單已收款，請在訂單中退款：{orders}',
        openWaitlist: '候補名單',
        errors: {
          SERIES_ENDED: '此循環預約已完結',
          NOTHING_TO_CANCEL: '沒有仍有效的未來堂數可取消',
          FORBIDDEN: '你沒有權限執行此操作',
          NOT_FOUND: '此預約已不存在',
        },
      },
    },
  },

  loyalty: {
    title: '忠誠度計劃',
    subtitle: '獎勵客戶，提升回購率',

    filters: {
      member: '會員',
      topTier: 'VIP 級',
      highValueNonMember: '高消費未入會',
    },
    sort: {
      lifetimePoints: '累積積分',
    },

    tier: {
      label: '會員等級',
      notMember: '尚未成為會員',
      nextTier: '下一級：{name}',
      pointsToNext: '距離 {name} 還差 {count} 分',
      benefits: '會員權益',
      rankFallback: '第 {rank} 級',
    },

    points: {
      balance: '可用積分',
      lifetime: '累積積分',
      earned: '+{count} 分',
      redeemed: '−{count} 分',
      expiringSoon: '{count} 分將於 {date} 到期',
      label: '積分',
      labelShort: '分',
    },

    member: {
      enrolledSince: '自 {date} 加入',
      pending: '待處理',
      paused: '已暫停',
      expired: '已過期',
      banned: '已停權',
    },

    program: {
      title: '計劃',
      create: '建立計劃',
      edit: '編輯計劃',
      active: '使用中',
      inactive: '未啟用',
      autoEnroll: {
        label: '自動加入會員',
        description: '客戶首次符合條件的消費將自動成為會員。',
      },
    },

    backfill: {
      stepTitle: '過往消費記錄',
      stepSubtitle: '想如何處理舊客戶的消費歷史？',

      optionForward: '從今日起計',
      optionForwardDescription: '之後的新交易才會累積積分',
      option3Months: '過去 3 個月',
      option6Months: '過去 6 個月',
      option12Months: '過去 12 個月',
      optionAll: '所有歷史交易',
      recommended: '建議',

      previewLoading: '計算中…',
      previewLabel: '預計會處理：',
      previewOrders: '{count} 筆訂單',
      previewAppointments: '{count} 個預約',
      previewMembers: '{count} 位客戶將自動成為會員',
      previewPoints: '將發出 {count} 分',
      previewTierUpgrades: '{count} 位客戶將升級',
      previewEmpty: '此範圍內無符合條件的交易',

      confirmTitle: '確認回溯計分',
      confirmDescription: '一旦開始將無法復原。客戶下次接觸時將看到會員身份。',
      confirmCta: '開始回溯',

      running: '回溯進行中…',
      completedTitle: '回溯完成',
      completedDescription: '已處理 {orders} 筆訂單與 {appointments} 個預約，{members} 位客戶加入會員，發出 {points} 分。',

      disclosure: '過往交易將根據目前的累積比率轉換為積分。客戶下次接觸時將看到會員身份。',
    },

    empty: {
      members: {
        title: '尚無會員',
        description: '客戶開始累積積分後將會在此顯示。',
      },
      rewards: {
        title: '尚未設定獎賞',
        description: '加入客戶可用積分兌換的獎賞。',
        cta: '新增獎賞',
      },
      history: {
        title: '尚無活動記錄',
        description: '累積、兌換與到期記錄將在此顯示。',
      },
    },

    errors: {
      insufficientPoints: '積分不足',
      notEnrolled: '尚未加入此計劃',
      programInactive: '此計劃目前未啟用',
      backfillFailed: '回溯失敗，請再試一次。',
    },

    reasons: {
      order_paid: '訂單付款',
      order_completed: '訂單完成',
      appointment_completed: '預約完成',
      signup_bonus: '入會獎勵',
      birthday_bonus: '生日獎勵',
      review_bonus: '評價獎勵',
      referral_earn: '推薦獎勵',
      tier_upgrade_bonus: '升級獎勵',
      stamp_earned: '集點章',
      stamp_complete_bonus: '集點卡完成',
      manual_adjustment: '人工調整',
      bonus: '額外獎勵',
      redemption_hold: '兌換預留',
      redemption_commit: '已兌換',
      redemption_cancel: '兌換取消',
      expiry: '積分到期',
      backfill_order: '過往訂單',
      backfill_appointment: '過往預約',
    },
  },

  memberPortal: {
    signIn: {
      title: '登入會員帳號',
      subtitle: '請輸入你之前在這家商家使用過的電子郵件或電話，我們會寄送一次性驗證碼。',
      contactLabel: '電子郵件或電話號碼',
      contactPlaceholder: 'you@example.com 或 +886 912 345 678',
      sendCode: '發送驗證碼',
      sending: '傳送中…',
      contactInvalid: '請輸入正確的電子郵件或電話號碼。',
      rateLimited: '請求過於頻繁，請一小時後再試。',
      genericError: '發生錯誤，請再試一次。',
      privacyNote: '只有你之前提供給商家的電子郵件／電話才能登入。',
      waVerify: {
        primaryCta: '使用 WhatsApp 繼續',
        emailFallback: '改用電子郵件',
        phoneLabel: '手機號碼',
        phonePlaceholder: '+886 912 345 678',
        phoneInvalid: '請輸入有效的手機號碼。',
        initSubmit: '繼續',
        initSubmitting: '準備中…',
        verifyTitle: '使用 WhatsApp 驗證',
        verifySubtitle: '點擊下方按鈕，一鍵發送驗證訊息給我們。訊息送達後我們會自動為你登入。',
        tapToSend: '點此透過 WhatsApp 發送',
        qrLabel: '或用手機掃描下方 QR code',
        expiresIn: '{seconds} 秒後過期',
        expired: '驗證已過期，請重新開始。',
        mismatch: '你輸入的電話與發送訊息的 WhatsApp 帳號不一致，請重試。',
        changePhone: '更換號碼',
        waiting: '等待你的訊息…',
        verified: '已驗證 — 正為你登入。',
      },
    },
    verify: {
      title: '輸入驗證碼',
      subtitle: '我們已將 6 位數字驗證碼寄送至 {contact}，5 分鐘內有效。',
      codeLabel: '驗證碼',
      codePlaceholder: '123456',
      submit: '驗證',
      submitting: '驗證中…',
      resend: '重新發送',
      resendIn: '{seconds} 秒後可重新發送',
      invalidCode: '驗證碼不正確，請再試一次。',
      tooManyAttempts: '嘗試次數過多，請重新申請。',
      edit: '使用其他聯絡方式',
    },
    dashboard: {
      welcomeBack: '歡迎回來，{name}',
      welcomeBackNoName: '歡迎回來',
      balanceLabel: '可用點數',
      lifetimeLabel: '累積點數',
      progressToNext: '距離 {name} 還差 {count} 點',
      atTopTier: '你已經到達最高等級，感謝你長期支持。',
      yourRewardsHeader: '可兌換獎賞',
      viewAllRewards: '查看全部獎賞',
      recentActivityHeader: '最近活動',
      viewAllActivity: '查看全部記錄',
      noRecentActivity: '尚無活動 — 你的累積、兌換記錄會在這裡顯示。',
    },
    rewards: {
      title: '獎賞',
      emptyTitle: '目前尚無獎賞',
      emptyDescription: '商家會持續新增獎賞，請之後再回來看看。',
      pointsCost: '{points} 點',
      redeem: '兌換',
      redeeming: '處理中…',
      insufficient: '點數不足',
      confirmTitle: '確認兌換',
      confirmDescription: '使用 {points} 點兌換 {name}？兌換後會出現一組獎賞編號，請在店內出示給服務人員。',
      confirmCta: '確認兌換',
      successTitle: '兌換成功',
      successDescription: '請在店內出示下列編號：',
      voucherCodeLabel: '獎賞編號',
      voucherExpiresLabel: '有效至 {date}',
      backToRewards: '返回獎賞列表',
    },
    history: {
      title: '活動記錄',
      filterAll: '全部',
      filterEarn: '累積',
      filterRedeem: '兌換',
      filterExpiry: '到期',
      emptyTitle: '尚無活動',
      emptyDescription: '你的累積、兌換、到期記錄會在這裡顯示。',
      loadMore: '載入更多',
    },
    settings: {
      title: '設定',
      languageSectionTitle: '語言',
      languageSectionHint: '只影響此裝置的顯示，不會變更商家端的設定。',
      dataSectionTitle: '你的資料',
      exportData: '下載我的忠誠度資料',
      exportFormatJson: 'JSON',
      exportFormatCsv: 'CSV',
      exportHint: '完整的累積、兌換、到期記錄副本，方便你自行保存。',
      accountSectionTitle: '帳戶',
      signOut: '登出',
      signingOut: '登出中…',
    },
    nav: {
      dashboard: '首頁',
      bookings: '預約',
      orders: '訂單',
      offers: '優惠',
      rewards: '獎賞',
      history: '記錄',
      settings: '設定',
    },
    offers: {
      title: '我的優惠',
      walletHeader: '你的優惠',
      publicHeader: '公開優惠',
      historyHeader: '已使用',
      emptyTitle: '暫時沒有優惠',
      emptyDescription: '你的優惠會在此顯示。',
      claimTitle: '有優惠碼？',
      claimPlaceholder: '輸入優惠碼',
      claimSubmit: '領取',
      claimSubmitting: '領取中…',
      claimSuccess: '已加入你的優惠包',
      expiresLabel: '有效期至 {date}',
      noExpiry: '長期有效',
      minSpendLabel: '最低消費 {amount}',
      autoApplyTag: '自動套用',
      copyCode: '複製碼',
      codeCopied: '已複製！',
      codeCopiedAnnouncement: '優惠碼 {code} 已複製到剪貼簿',
      useNow: '立即使用',
      useNowToastTitle: '優惠已套用',
      useNowToastDescription: '已加入購物車，結帳即時生效。',
      identityHint: '已登入：{contact}',
      offerStatusAvailable: '可用',
      offerStatusReserved: '使用中',
      offerStatusUsed: '已用',
      offerStatusExpired: '已過期',
      offerStatusRevoked: '已撤回',
      discountPercent: '{value}% off',
      discountPercentCap: '{value}% off（最多減 {cap}）',
      discountFixed: '減 {value}',
      discountFreeShipping: '免運費',
      historySavedLabel: '已節省 {amount}',
      discountBogoFree: '買{n}送1',
      discountBogoPct: '買{n}件 最低價一件{pct}% off',
      loyaltyHeader: '積分換領',
      loyaltyHint: '以積分換領的獎賞券 — 結帳時可以使用。',
      loyaltyClaimedTag: '已套用至訂單',
      loyaltyCounterTag: '到店使用',
    },
    bookings: {
      title: '我的預約',
      upcomingHeader: '即將到來',
      pastHeader: '歷史',
      emptyTitle: '還沒有預約',
      emptyDescription: '即將到來與歷史預約都會在這裡顯示。',
      browseServices: '瀏覽服務',
      statusPending: '等待商戶確認',
      statusConfirmed: '已確認',
      statusCompleted: '已完成',
      statusCancelled: '已取消',
      statusNoShow: '未到場',
      detail: {
        title: '預約詳情',
        referenceLabel: '預約編號',
        backToList: '回到預約列表',
        notesLabel: '備註',
        depositLabel: '訂金',
        manageHeader: '管理',
        cancelButton: '取消預約',
        cancelling: '取消中…',
        cancelConfirmTitle: '確定要取消？',
        cancelConfirmBody: '您即將取消預約 {reference}，無法復原。',
        cancelConfirmCta: '是，取消',
        cancelKeep: '保留預約',
        cancelReasonLabel: '原因（選填）',
        cancelReasonPlaceholder: '想對商家說的話',
        cancelSuccess: '預約已取消。',
        cancelError: '無法取消，請再試一次。',
        outsideWindowError: '已超過取消時限，請直接聯絡商家。',
        contactShopToCancel: '此預約無法在網上取消，請聯絡商家辦理。',
        cancelWindowNote: '預約前 {hours} 小時或更早取消可全額退款。',
        cancelDisabledNote: '此商家不接受線上取消，請直接聯絡。',
        withdrawRequestNote: '商家尚未確認此預約，你可以隨時撤回申請。',
        cancelSuccessRefundLine: '依據取消政策，您將獲退款 {pct}%。商戶會於數個工作天內處理。',
        cancelSuccessNoRefund: '依據取消政策，此次取消不予退款。',
        cancelSuccessNoPolicy: '取消已記錄。退款安排請聯絡商戶確認。',
        cancelSuccessClose: '完成',
        verifyToManageNote: '驗證手機後即可在此管理預約。',
        verifyToManageCta: '立即驗證',
        contactMerchantHeader: '聯絡商家',
        contactWhatsApp: '開啟 WhatsApp',
      },
    },
    orders: {
      title: '我的訂單',
      activeHeader: '進行中',
      pastHeader: '歷史',
      emptyTitle: '還沒有訂單',
      emptyDescription: '透過商家下單後，訂單會顯示在這裡。',
      browseProducts: '瀏覽商品',
      itemsSummary: '{count} 件商品',
      statusPending: '待付款',
      statusConfirmed: '已確認',
      statusPaid: '已付款',
      statusShipped: '已出貨',
      statusCompleted: '已完成',
      statusCancelled: '已取消',
      statusExpired: '已過期',
      statusRefunded: '已退款',
    },
    poweredBy: '由 NovixOne 提供',

    receiptCta: {
      title: '追蹤你的點數',
      description: '隨時查看點數、瀏覽獎賞、自助領取獎賞編號。',
      button: '進入會員主頁',
    },

    voucherCommit: {
      title: '使用獎賞',
      codeLabel: '獎賞編號',
      codePlaceholder: 'ABC123',
      lookup: '查詢',
      lookingUp: '查詢中…',
      foundFor: '由 {name} 兌換',
      pointsHeld: '已預留 {points} 點',
      discountValue: '折扣面值 {value}',
      alreadyOnOrder: '此券已在結帳時套用到訂單，請勿再人手減價。',
      voucherExpires: '有效至 {date}',
      commitCta: '確認使用',
      committing: '處理中…',
      cancelCta: '取消',
      successTitle: '獎賞已使用',
      successDescription: '點數已正式扣除，會員的獎賞記錄已更新。',
      errorNotFound: '找不到此獎賞編號。',
      errorAlreadyUsed: '此獎賞已使用過。',
      errorExpired: '此獎賞已過期。',
      errorGeneric: '無法處理，請再試一次。',
    },
  },

  livechat: {
    launcher: {
      contactUs: '聯絡我們',
      needHelp: '需要協助？',
    },
    picker: {
      title: '想如何聯絡我們？',
      whatsapp: 'WhatsApp',
      whatsappSub: '用 WhatsApp 開啟',
      line: 'LINE',
      lineSub: '用 LINE 開啟',
      webChat: '在此對話',
      webChatSubAnonymous: '直接在此頁回覆',
      webChatSubSignedIn: 'Web Chat（已登入）',
    },
    widget: {
      ariaLabel: '即時對話',
      closeAria: '關閉對話',
      loading: '載入中…',
      loadError: '無法載入對話。請重新整理再試。',
      poweredBy: 'Powered by NovixOne',
    },
    signin: {
      continueGoogle: '使用 Google 繼續',
      continueApple: '使用 Apple 繼續',
      continueEmail: '使用電郵繼續',
      emailHint: '我們會以電郵寄送一個 6 位數字驗證碼給你。',
      emailPlaceholder: 'you@example.com',
      sendCode: '寄出驗證碼',
      sending: '寄送中…',
      back: '← 返回',
      codeSentTo: '驗證碼已寄到 {contact}。請查收電郵。',
      otpPlaceholder: '123456',
      verify: '驗證',
      verifying: '驗證中…',
      errorInvalidEmail: '請輸入有效的電郵地址',
      errorSendFailed: '無法寄出驗證碼，請再試。',
      errorWrongCode: '驗證碼錯誤，請再試。',
      errorCodeNotSixDigits: '驗證碼要 6 位數字。',
      errorTooManyAttempts: '嘗試次數太多，請重新寄送。',
      errorCodeExpired: '驗證碼已過期，請重新寄送。',
      errorVerifyFailed: '驗證失敗。',
    },
    conversation: {
      inputPlaceholder: '輸入訊息…',
      sendButton: '傳送',
      sendingButton: '…',
      sendFailedSuffix: '（傳送失敗）',
      defaultGreeting: '你好！我可以協助你查詢預約、產品及訂單。選擇一個方式登入即可開始對話。',
    },
  },
}

export default dict
